import _ from "lodash";
import moment from "moment";
import {
  SPECIAL_SUBLOCATIONS,
  SUBLOCATION_WORKPERFORMED_TYPE,
} from "../../components/AdrV3/constants/adrConstantsV3";
import {
  getSpecialLocationOptionValue,
  MAX_COLUMNS,
} from "../../components/AdrV3/utils/adrUtilsV3";
import "../../_typedefs/adrV3TypeDefs";
import { stringifyJSON, tryParseJSON } from "../../_utils/generalUtils";

/****************************************************************************************
 * Provides methods operating on Adr.
 * If the constructor param is null, then
 *
 * @param {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} adr - not adrDto!
 ***************************************************************************************/
export function AdrFactory(adr = null) {
  /** @type {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
  this.data =
    !adr || typeof adr !== "object"
      ? {}
      : {
          ...adr,
          /** @type {import("../../_typedefs/adrV3TypeDefs").WorkPerformedClient} */
          workPerformedClient: adr.workPerformedClient
            ? _.cloneDeep(adr.workPerformedClient)
            : {},
          /** @type {import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient[]} */
          laborSet: adr.laborSet
            ? adr.laborSet.map((lsr) => ({
                ...lsr,
                laborHours: lsr.laborHours ? _.cloneDeep(lsr.laborHours) : {},
                // Always init remarks w/id=0 and remarks="", BE will delete all
                // remarks that are empty.
                remarks: lsr.remarks
                  ? { ...lsr.remarks }
                  : { laborRemarksId: 0, remarks: "" },
              }))
            : [],
          /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient[]} */
          equipmentSet: adr.equipmentSet
            ? adr.equipmentSet.map((esr) => ({
                ...esr,
                equipmentHours: esr.equipmentHours ? _.cloneDeep(esr.equipmentHours) : {},
                operators: esr.operators ? _.cloneDeep(esr.operators) : {},
                remarks: esr.remarks
                  ? { ...esr.remarks }
                  : { equipmentRemarksId: 0, remarks: "" },
              }))
            : [],
          /** @type {import("../../_typedefs/adrV3TypeDefs").GeneralComment[]} */
          generalComments: adr.generalComments.map((gc) => ({
            ...gc,
            keywordIds: [...gc.keywordIds],
            commentAttachments: gc.commentAttachments.map((gca) => ({ ...gca })),
          })),
          clarificationComments: adr.clarificationComments.map((cc) => ({
            ...cc,
            keywordIds: [...cc.keywordIds],
          })),
          correctionComments: adr.correctionComments.map((cc) => ({ ...cc })),
          tentativeAgreements: adr.tentativeAgreements.map((ta) => ({
            ...ta,
            contractors: _.cloneDeep(ta.contractorsClient || {}),
            laborsClient: _.cloneDeep(ta.laborsClient || {}),
            equipmentClient: _.cloneDeep(ta.equipmentClient || {}),
            materialsClient: _.cloneDeep(ta.materialsClient || {}),
            attachments: (ta.attachments || []).map((taa) => ({ ...taa })),
          })),
          history: (adr.history || []).map((h) => ({ ...h })),
        };

  /**
   * Maps incoming adr into a client-optimized shape (i.e. create
   * workPerformedClient based on workPerformed, add laborHours to each
   * laborSetClient, etc.)
   * @param {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportDto} adrDto
   *        - in the shape of AdrDto from backend
   */
  this.initClient = (
    adrDto,
    projItemList = null,
    projCcoList = null,
    projPendingIssueList = null,
    projLaborList = null,
    projEquipList = null
  ) => {
    if (!adrDto || typeof adrDto !== "object") return;

    /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").WorkPerformedClient>} */
    let workPerformedClient = {};

    // An array of wpTabIds to help with iterating thru available lwp/ewp's (available
    // after wpClient is fully initialized.)
    /** @type {number[]} */
    let wpTabIds = [];

    // LaborSetDto[] will be mapped into Object.<string, LaborSetRowClient>.
    // The string keys are rowKeys, which is client-specific (it is used as a unique
    // element id by React and react-beautiful-dnd). Although rowKey is client-specific,
    // setting the rowKey for each lsrDto here (rather than when initializing
    // laborSetClient) makes initilization of laborSetOrder (see below) more efficient
    // (since it is in an array form, not an object like laborSetClient).
    // Note: Below line may need to change if another DnD-based item ordering method
    // is used, since it is dependent on how react-beautiful-dnd manages item ordering
    // (which is an array of string keys, in an order that represents item order).
    /** @type {import("../../_typedefs/adrV3TypeDefs").LaborSetRowDto[]} */
    let laborSetDto = adrDto.laborSet
      ? adrDto.laborSet.map((lsr, index) => ({ ...lsr, rowKey: `lsrKey_${index + 1}` }))
      : [];

    // Row order is established by a string[] of ordered rowKeys of laborSetClient.
    // (The 'rowOrder' property of each laborSetClient is also updated whenever the order
    // changes, but is used for persistence only - not for rendering.)
    /** @type {string[]} */
    const laborSetOrder = _.orderBy(laborSetDto, "rowOrder", "asc").map(
      (lsrDto) => lsrDto.rowKey
    );

    // Do the same for equipmentSetDto, as above.
    /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowDto[]} */
    let equipmentSetDto = adrDto.equipmentSet
      ? adrDto.equipmentSet.map((esr, index) => ({
          ...esr,
          rowKey: `esrKey_${index + 1}`,
        }))
      : [];

    /** @type {string[]} */
    const equipmentSetOrder = _.orderBy(equipmentSetDto, "rowOrder", "asc").map(
      (esrDto) => esrDto.rowKey
    );

    /** @type {import("../../_typedefs/adrV3TypeDefs").LaborWorkPerformedDto[]} */
    let allLwps = []; // Temp collection of all flattened laborWps in the adrDto

    /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentWorkPerformedDto[]} */
    let allEwps = []; // Temp collection of all flattened equipmentWps in the adrDto

    /**
     * An internal helper function to generate wpCaptions, depending on the type
     * of workPerformed (item/CCO/PI)
     * @param {import("../../_typedefs/adrV3TypeDefs").WorkPerformedDto} wp
     *        - WorkPerformedDto provided by backend has no captions
     * @returns {import("../../_typedefs/adrV3TypeDefs").WorkPerformedCaptions}
     */
    const getWpCaptions = (wp) => {
      /** @type {import("../../_typedefs/adrV3TypeDefs").WorkPerformedCaptions} */
      const defaultReturnVal = {
        wpShortCaption: "(N/A)",
        wpFullCaption: "(N/A)",
        wpShortId: "(N/A)",
        unitOfMeasure: "",
      };
      const isItem = wp.itemId && !wp.ccoId && !wp.pendingIssueId;
      const isCco = !wp.itemId && wp.ccoId && !wp.pendingIssueId;
      const isPi = !wp.itemId && !wp.ccoId && wp.pendingIssueId;
      if (isItem) {
        if (!projItemList) return defaultReturnVal;
        const item = (projItemList || []).find((i) => i.bidItemId === wp.itemId);
        return {
          wpShortCaption: item ? item.itemShortCaption : "(N/A)",
          wpFullCaption: item ? item.itemFullCaption : "(N/A)",
          wpShortId: item ? item.itemShortId : "(N/A)",
          unitOfMeasure: item ? item.unitOfMeasure : "",
        };
      } else if (isCco) {
        if (!projCcoList) return defaultReturnVal;
        const cco = (projCcoList || []).find((c) => c.ccoId === wp.ccoId);
        return {
          wpShortCaption: cco ? cco.ccoShortCaption : "(N/A)",
          wpFullCaption: cco ? cco.ccoFullCaption : "(N/A)",
          wpShortId: cco ? cco.ccoShortId : "(N/A)",
          unitOfMeasure: "",
        };
      } else if (isPi) {
        if (!projPendingIssueList) return defaultReturnVal;
        const pi = (projPendingIssueList || []).find(
          (pi) => pi.pendingIssueId === wp.pendingIssueId
        );
        return {
          wpShortCaption: pi ? pi.pendingIssueShortCaption : "(N/A)",
          wpFullCaption: pi ? pi.pendingIssueFullCaption : "(N/A)",
          wpShortId: pi ? pi.pendingIssueShortId : "(N/A)",
          unitOfMeasure: "",
        };
      }
      return defaultReturnVal;
    };

    // 1. Initialize workPerformedClient
    if (adrDto.workPerformed && Array.isArray(adrDto.workPerformed)) {
      // Loop thru all wpDto and copy into client; also copy locWp (but not
      // labWp and equipWp - they are copied into laborSet and equipmentSet.)
      // Below, i+1 = wpTabId
      adrDto.workPerformed.forEach((wpDto, i) => {
        const wpTabId = i + 1; // wpTabId should never be 0
        const wpCaptions = getWpCaptions(wpDto);

        // Prepare subLocWps. First, look for any locWpDto's with subLocWp's; and
        // 1) flatten as locWpClient for special subLocWps, or 2) collect in
        // nestedSublocations[] under locWpClient for non-special subLocWps.
        // The second case can be handled at the same time as mapping of parent
        // locWpDto into locWpDtoClient.

        // Below, handle the first case for special subLocWps:
        /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} */
        const specialSubLocWpClient = wpDto.locationWorkPerformed
          // Filter only locWps that contain subLocWps, and ones that contain special
          // subLocWps. (The .some() operation is only here so locWpDtos that do not
          // have special subLocWps are screened out from the beginning.)
          .filter(
            (locWpDto) =>
              locWpDto.sublocationWorkPerformed &&
              locWpDto.sublocationWorkPerformed.length > 0 &&
              locWpDto.sublocationWorkPerformed.some((subLocWpDto) =>
                SPECIAL_SUBLOCATIONS.includes(subLocWpDto.sublocationWorkPerformedType)
              )
          )
          .map((locWpDto) => [...locWpDto.sublocationWorkPerformed])
          .flat()
          // Filter only the special subLocWps - this is just in case subLocWpType
          // is accidentally not provided. Theoretically, this filter should return
          // 100% of the flattened items from previous operations.
          .filter((subLocWpDto) =>
            SPECIAL_SUBLOCATIONS.includes(subLocWpDto.sublocationWorkPerformedType)
          )
          .map((subLocWpDto) => ({
            // DO NOT SET clientId HERE. It is set when special and non-special locWps
            // are combined later.
            workPerformedId: subLocWpDto.locationWorkPerformedWorkPerformedId,
            // For special subLocWps, locationId represents the type of subLocWp,
            // not the actual locationId of alignment (or coordinate, etc.) This is
            // only is used to set the value of location selector in locWpr.
            locationId: getSpecialLocationOptionValue(
              subLocWpDto.sublocationWorkPerformedType
            ),
            isSublocation: true,
            sublocationWorkPerformedType: subLocWpDto.sublocationWorkPerformedType,
            sublocationWorkPerformedId: subLocWpDto.sublocationWorkPerformedId,
            // Below, sublocationId here is the actual locationId of the alignment. For
            // coordinates, it is the locationId of the general coordinate location.
            sublocationId: subLocWpDto.locationWorkPerformedLocationId,
            workDescription: subLocWpDto.workDescription,
            workDescriptionDraftState: subLocWpDto.workDescriptionDraftState,
            inspectionType: subLocWpDto.inspectionType,
            quantity: subLocWpDto.quantity || 0,
            isControllingOperation: subLocWpDto.isControllingOperation,
            isDisputed: subLocWpDto.isDisputed,
            // For special subLocWp, all of the station/coordinate fields are used:
            stationFromOrAt: subLocWpDto.stationFromOrAt,
            stationFromOrAtOffsetValue: subLocWpDto.stationFromOrAtOffsetValue,
            stationFromOrAtOffsetDirection: subLocWpDto.stationFromOrAtOffsetDirection,
            stationTo: subLocWpDto.stationTo,
            stationToOffsetValue: subLocWpDto.stationToOffsetValue,
            stationToOffsetDirection: subLocWpDto.stationToOffsetDirection,
            attachments: subLocWpDto.sublocationAttachments
              ? subLocWpDto.sublocationAttachments.map((subLocWpaDto) => ({
                  ...subLocWpaDto,
                }))
              : [],
            miscMaterials: subLocWpDto.sublocationMiscMaterials
              ? subLocWpDto.sublocationMiscMaterials.map((subLocMmDto) => ({
                  ...subLocMmDto,
                }))
              : [],
            testsPerformed: subLocWpDto.sublocationTestsPerformed
              ? subLocWpDto.sublocationTestsPerformed.map((subLocTpDto) => ({
                  ...subLocTpDto,
                }))
              : [],
            nestedSublocations: [],
          }));

        // Then handle the second case for nested sublocations along with
        // mapping of regular locWpDto into locWpClient:
        /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} */
        const locWpClient = wpDto.locationWorkPerformed
          // Below filter is needed to screen out any parent locWp hosting special subloc
          // children. Since the special subloc children are elevated and flattened,
          // parents do not need to be rendered on screen.
          .filter(
            (locWpDto) =>
              !locWpDto.sublocationWorkPerformed.some((subLocWp) =>
                SPECIAL_SUBLOCATIONS.includes(subLocWp.sublocationWorkPerformedType)
              )
          )
          .map((locWpDto) => ({
            workPerformedId: locWpDto.workPerformedId,
            locationId: locWpDto.locationId,
            isSublocation: false,
            workDescription: locWpDto.workDescription,
            workDescriptionDraftState: locWpDto.workDescriptionDraftState,
            inspectionType: locWpDto.inspectionType,
            quantity: locWpDto.quantity || 0,
            // Since this is parent-level locWp, 'isControllingOperation' should be set.
            isControllingOperation: locWpDto.isControllingOperation,
            isDisputed: locWpDto.isDisputed,
            // Ignore all sublocation-related fields at this level; handle them
            // below in the mapping of nestedSublocations
            attachments: locWpDto.locationWorkPerformedAttachments
              ? locWpDto.locationWorkPerformedAttachments.map((locWpaDto) => ({
                  ...locWpaDto,
                }))
              : [],
            miscMaterials: locWpDto.locationMiscMaterials
              ? locWpDto.locationMiscMaterials.map((locMmDto) => ({
                  ...locMmDto,
                }))
              : [],
            testsPerformed: locWpDto.locationTestsPerformed
              ? locWpDto.locationTestsPerformed.map((locTpDto) => ({
                  ...locTpDto,
                }))
              : [],
            nestedSublocations: locWpDto.sublocationWorkPerformed
              ? locWpDto.sublocationWorkPerformed.map((subLocWpDto) => ({
                  sublocationWorkPerformedId: subLocWpDto.sublocationWorkPerformedId,
                  sublocationId: subLocWpDto.sublocationId,
                  sublocationWorkperformedType:
                    SUBLOCATION_WORKPERFORMED_TYPE.NESTEDLOCATION,
                  workDescription: subLocWpDto.workDescription,
                  workDescriptionDraftState: subLocWpDto.workDescriptionDraftState,
                  inspectionType: subLocWpDto.inspectionType,
                  quantity: subLocWpDto.quantity || 0,
                  isControllingOperation: subLocWpDto.isControllingOperation,
                  isDisputed: subLocWpDto.isDisputed,
                  // Nested sublocations do not need station/coordinate fields,
                  // since they are pre-defined.
                  miscMaterials: subLocWpDto.sublocationMiscMaterials
                    ? subLocWpDto.sublocationMiscMaterials.map((subLocMm) => ({
                        ...subLocMm,
                      }))
                    : [],
                  testsPerformed: subLocWpDto.sublocationTestsPerformed
                    ? subLocWpDto.sublocationTestsPerformed.map((subLocTp) => ({
                        ...subLocTp,
                      }))
                    : [],
                  attachments: subLocWpDto.sublocationAttachments
                    ? subLocWpDto.sublocationAttachments.map((sla) => ({ ...sla }))
                    : [],
                }))
              : [],
          }));

        // This is the initialized array of locWpClient.
        const locationWorkPerformed = [...locWpClient, ...specialSubLocWpClient];
        // Set clientId for each locWpc. This is also where the rowOrder can be set (future).
        locationWorkPerformed.forEach((locWpc, index) => (locWpc.clientId = index + 1));

        // Since this is the loop where wpTabId is first generated, insert wpTabId
        // into each lwp and ewp
        wpDto.laborWorkPerformed.forEach((lwpDto) => (lwpDto.wpTabId = wpTabId));
        wpDto.equipmentWorkPerformed.forEach((ewpDto) => (ewpDto.wpTabId = wpTabId));

        workPerformedClient[wpTabId] = {
          workPerformedId: wpDto.workPerformedId,
          wpTabId,
          sortOrder: i, //wp.sortOrder, // Actual sorting is done by client
          itemId: wpDto.itemId,
          ccoId: wpDto.ccoId,
          pendingIssueId: wpDto.pendingIssueId,
          isIdle: wpDto.isIdle,
          wpShortCaption: wpCaptions.wpShortCaption,
          wpFullCaption: wpCaptions.wpFullCaption,
          wpShortId: wpCaptions.wpShortId,
          unitOfMeasure: wpCaptions.unitOfMeasure,
          locationWorkPerformed,
        };

        // Build an array of wpTabIds to help with lwp/ewp tasks below
        wpTabIds.push(wpTabId);
      });

      // Extract all laborWps from each wp, then flatten. They will be iterated thru
      // to build laborHours for each laborSetRow below.
      allLwps = adrDto.workPerformed
        .map((wpDto) => wpDto.laborWorkPerformed.map((lwp) => ({ ...lwp })))
        .flat();

      // Same with equipmentWps
      allEwps = adrDto.workPerformed
        .map((wpDto) => wpDto.equipmentWorkPerformed.map((ewp) => ({ ...ewp })))
        .flat();
    }

    // 2. Initialize laborSetClient (adds laborHours and remarks to each laborSetRow,
    //    into a client-optimized shape: Object.<string, LaborSetRowClient>). Note that
    //    rowKey has been created for each laborSetDto already (see above).
    /** @type {Object.<string,import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient>} */
    const laborSetClient = {}; // Initialize blank
    laborSetDto.forEach((lsrDto) => {
      // Create client-optimized labor hours object. Labor hours data are specific
      // to the current lsrDto.
      /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").LaborHours>} */
      let laborHours = {}; // Initialize blank

      wpTabIds.forEach((wpTabId) => {
        // Find exact match
        const matchingLwp = allLwps.find(
          (lwp) =>
            lwp.laborId === lsrDto.laborId &&
            lwp.tradeId === lsrDto.tradeId &&
            lwp.wpTabId === wpTabId
        );
        // If no matchingLwp is found, do not skip - still initialize as a blank field.
        // (This would be the case for a blank lsr that is being loaded - lsr is saved
        // as LaborSet in db, but there are no lwp's associated with this row's labor
        // or trade because neither of them exist.)
        laborHours[wpTabId] = {
          wpTabId: +wpTabId,
          regHours: matchingLwp ? matchingLwp.regHours : "",
          otHours: matchingLwp ? matchingLwp.otHours : "",
        };
      });

      // Create client-optimized labor remarks.
      const laborRemarks = adrDto.laborRemarks
        ? adrDto.laborRemarks.find(
            (lr) => lr.laborId === lsrDto.laborId && lr.tradeId === lsrDto.tradeId
          )
        : null;
      /** @type {import("../../_typedefs/adrV3TypeDefs").LaborRemarksClient} */
      const remarks = {
        // Always init remarks w/id=0 and remarks="" if !exists
        laborRemarksId: laborRemarks ? laborRemarks.laborRemarksId : 0,
        remarks: laborRemarks ? laborRemarks.remarks : "",
      };

      // Get contractorId from laborId
      const labor = (projLaborList || []).find((l) => l.laborId === lsrDto.laborId);
      const contractorId = labor ? labor.contractorId : 0;

      // Create new key-value in laborSetClient:
      laborSetClient[lsrDto.rowKey] = {
        ...lsrDto, // includes rowKey already
        contractorId,
        laborHours,
        remarks,
        showOvertimeRow:
          Object.values(laborHours).filter((lh) => +lh.otHours > 0).length > 0,
      };
    });

    // 3. Initialize equipmentSetClient. Similar to laborSet above, but add operator data.
    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient>} */
    const equipmentSetClient = {};
    equipmentSetDto.forEach((esrDto) => {
      /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").EquipmentHours>} */
      let equipmentHours = {};

      // MULTIPLE OPERATORS: FOR FUTURE USE
      // /** @type {Object.<number, number[]>} */
      // let operators = {};

      wpTabIds.forEach((wpTabId) => {
        // Find exact match
        const matchingEwp = allEwps.find(
          (ewp) => ewp.equipmentId === esrDto.equipmentId && ewp.wpTabId === wpTabId
        );
        // If no matchingLwp is found, do not skip - still initialize as a blank field.
        // (This would be the case for a blank lsr that is being loaded - lsr is saved
        // as LaborSet in db, but there are no lwp's associated with this row's labor
        // or trade because neither of them exist.)
        equipmentHours[wpTabId] = {
          wpTabId: +wpTabId,
          regHours: matchingEwp ? matchingEwp.regHours : "",
          otHours: matchingEwp ? matchingEwp.otHours : "",
          operatorIds: matchingEwp ? (matchingEwp.operatorIds || []) : [],
        };
      });

      const equipmentRemarks = adrDto.equipmentRemarks
        ? adrDto.equipmentRemarks.find((er) => er.equipmentId === esrDto.equipmentId)
        : null;
      /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentRemarksClient} */
      const remarks = {
        equipmentRemarksId: equipmentRemarks ? equipmentRemarks.equipmentRemarksId : 0,
        remarks: equipmentRemarks ? equipmentRemarks.remarks : "",
      };

      // Get contractorId from equipmentId
      const equip = (projEquipList || []).find(
        (e) => e.equipmentId === esrDto.equipmentId
      );
      const contractorId = equip ? equip.contractorId : 0;

      equipmentSetClient[esrDto.rowKey] = {
        ...esrDto,
        contractorId,
        equipmentHours,
        remarks,
        showOvertimeRow:
          Object.values(equipmentHours).filter((eh) => +eh.otHours > 0).length > 0,
      };
    });

    /**
     *
     * @param {string} locationsStr - stringified JSON of type {{locationId: number, sublocationWorkPerformedId: number?}[]}
     * @param {import("../../_typedefs/adrV3TypeDefs").WorkPerformedClient} wpc
     * @returns {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]>}
     */
    const buildTaLocations = (locationsStr, wpc) => {
      if (!wpc) return [];

      /** @type {{locationId: number, sublocationWorkPerformedId: number?}[]} */
      let locationsArr = [];
      try {
        locationsArr = JSON.parse(locationsStr || "[]");
      } catch (e) {
        console.error("Failed JSON.parse():", e);
        return [];
      }

      /** @type {Object.<number, {locationId: number, sublocationWorkPerformedId: number?}[]>} */
      let locationsObj = {};
      locationsArr.forEach((l) => {
        /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} */
        const locWp = (wpc.locationWorkPerformed || []).find((locWp) =>
          locWp.isSublocation
            ? locWp.sublocationId === l.locationId &&
              locWp.sublocationWorkPerformedId === l.sublocationWorkPerformedId
            : locWp.locationId === l.locationId
        );
        if (locWp) {
          locationsObj[locWp.clientId] = {
            locationId: locWp.isSublocation ? locWp.sublocationId : locWp.locationId,
            sublocationWorkPerformedId: locWp.isSublocation
              ? locWp.sublocationWorkPerformedId
              : null,
          };
        }
      });

      return locationsObj;
    };

    const buildTaContractorClient = (tacs) => {
      /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementContractorDto} */
      let taContractorList = {};
      tacs.forEach((tac) => (taContractorList[tac.contractorId] = { ...tac }));
      return taContractorList;
    };

    /**
     * Helper function to convert taLaborDto array into client-optimized shape.
     * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementLaborDto[]} tals
     * @returns {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementLaborClient>} - key is 'laborId.tradeId'
     */
    const buildTaLaborsClient = (tals) => {
      /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementLaborClient>} */
      let taLabors = {};
      tals.forEach((tal) => (taLabors[`${tal.laborId}.${tal.tradeId}`] = { ...tal }));
      return taLabors;
    };

    /**
     * Helper function to convert taEquipmentDto array into client-optimized shape.
     * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementEquipmentDto[]} taes
     * @returns {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementEquipmentClient>}
     */
    const buildTaEquipmentClient = (taes) => {
      /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementEquipmentClient>} */
      let taEquipmentList = {};
      taes.forEach((tae) => (taEquipmentList[tae.equipmentId] = { ...tae }));
      return taEquipmentList;
    };

    /**
     * Helper function to convert taMaterialDto array into client-optimized shape,
     * which is a normalized object with locationIds as keys, and taMaterialDtos grouped by
     * their locationIds as values.
     * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialDto[]} tams
     * @param {number} wpTabId
     * @returns
     */
    const buildTaMaterialsClient = (tamDtos) => {
      /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialClient[]} */
      let taMaterialsList = {};
      const locationIds = Array.from(new Set([...tamDtos.map((tam) => tam.locationId)]));
      locationIds.forEach((locId) => {
        const tamsForThisLoc = tamDtos
          .filter((tamDto) => tamDto.locationId === locId)
          .map(
            (tamDto, index) =>
              /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementMaterialClient} */
              ({
                clientId: index + 1,
                tentativeAgreementMaterialId: tamDto.tentativeAgreementMaterialId,
                tentativeAgreementId: tamDto.tentativeAgreementId,
                locationId: tamDto.locationId,
                locationMiscMaterialsId: tamDto.locationMiscMaterialsId,
                sublocationMiscMaterialsId: tamDto.sublocationMiscMaterialsId,
                changeJustification: tamDto.changeJustification,
                excludeFromTa: tamDto.excludeFromTa,
              })
          );
        taMaterialsList[locId] = tamsForThisLoc;
      });
      return taMaterialsList;
    };

    /**
     * Helper function to get wpTabId by workPerformedId
     */
    const getWpTabIdByWpId = (wpId) => {
      const wp =
        Object.values(workPerformedClient).find((wpc) => wpc.workPerformedId === wpId) ||
        null;
      return wp ? wp.wpTabId : 0;
    };

    const gcFactory = new GeneralCommentsFactory();
    gcFactory.initClient(adrDto.generalComments);

    const corrCommFactory = new CorrectionCommentFactory();
    corrCommFactory.initClient(adrDto.correctionComments);

    const clarCommFactory = new ClarificationCommentFactory();
    clarCommFactory.initClient(adrDto.clarificationComments);

    this.data = {
      ...adrDto,
      isReviewRevoked: false,
      // Convert date strings to date objects
      createdOn: new Date(adrDto.createdOn),
      reportStartDate: new Date(adrDto.reportStartDate),
      reportEndDate: new Date(adrDto.reportEndDate),
      draftSavedOn: adrDto.draftSavedOn ? new Date(adrDto.draftSavedOn) : null,
      submittedOn: adrDto.submittedOn ? new Date(adrDto.submittedOn) : null,
      reviewStartedOn: adrDto.reviewStartedOn ? new Date(adrDto.reviewStartedOn) : null,
      approvedOn: adrDto.approvedOn ? new Date(adrDto.approvedOn) : null,
      rejectedOn: adrDto.rejectedOn ? new Date(adrDto.rejectedOn) : null,
      supersededOn: adrDto.supersededOn ? new Date(adrDto.supersededOn) : null,
      // If weatherCondition is null, set as -1
      weatherCondition: adrDto.weatherCondition == null ? -1 : adrDto.weatherCondition,
      workPerformedClient,
      laborSetClient,
      laborSetOrder,
      equipmentSetClient,
      equipmentSetOrder,
      generalComments: gcFactory.data,
      clarificationComments: clarCommFactory.data,
      correctionComments: corrCommFactory.data,
      tentativeAgreements: adrDto.tentativeAgreements
        ? adrDto.tentativeAgreements
            .map((ta, index) => {
              const wpTabId = getWpTabIdByWpId(ta.workPerformedId);

              /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} */
              return {
                clientId: index + 1,
                tentativeAgreementId: ta.tentativeAgreementId,
                assistantDailyReportId: ta.assistantDailyReportId,
                isLocked: ta.isLocked,
                sequence: ta.sequence,
                createdOn: ta.createdOn,
                dateOfWork: ta.dateOfWork,
                wpTabId,
                isCustomTa: ta.isCustomTa,
                workPerformedId: ta.workPerformedId,
                workDescription: ta.workDescription,
                workDescriptionDraftState: ta.workDescriptionDraftState,
                locations: buildTaLocations(
                  ta.locations || "",
                  workPerformedClient[wpTabId]
                ),
                caltransRepSignature: ta.caltransRepSignature,
                isWetSignedByCaltransRep: ta.isWetSignedByCaltransRep,
                caltransRepSignedOn: ta.caltransRepSignedOn,
                caltransRepName: ta.caltransRepName,
                caltransRepRemarks: ta.caltransRepRemarks,
                isDisputedByCaltransRep: ta.isDisputedByCaltransRep,
                contractorsClient: buildTaContractorClient(ta.contractors || []),
                laborsClient: buildTaLaborsClient(ta.labors || []),
                equipmentClient: buildTaEquipmentClient(ta.equipment || []),
                materialsClient: buildTaMaterialsClient(ta.materials || []),
                attachments: (ta.attachments || []).map((taa) => ({
                  ...taa,
                })),
              };
            })
            .filter((ta) => ta.wpTabId)
        : [],
      history: adrDto.history ? adrDto.history.map((h) => ({ ...h })) : [],
      potentialReviewerUserIds: adrDto.potentialReviewerUserIds
        ? adrDto.potentialReviewerUserIds.map((r) => r)
        : [],
      sessionData: {},
    };

    // De-clutter properties unused by front-end:
    delete this.data.workPerformed;
    delete this.data.laborRemarks;
    delete this.data.equipmentRemarks;
    delete this.data.laborSet;
    delete this.data.equipmentSet;

    // console.log("adrClient out=", this.data);
  };

  /**
   * Maps client-optimized adr into adrDto for x-fer back to backend.
   * @returns {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportDto}
   */
  this.mapClientToDto = () => {
    /** @type {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportDto} */
    let adrDto = {};
    /** @type {import("../../_typedefs/adrV3TypeDefs").WorkPerformedDto[]} */
    let workPerformed = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").LaborSetRowDto[]} */
    let laborSet = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowDto[]} */
    let equipmentSet = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").LaborRemarksDto[]} */
    let laborRemarks = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentRemarksDto[]} */
    let equipmentRemarks = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").GeneralCommentDto[]} */
    let generalComments = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").CorrectionCommentDto[]} */
    let correctionComments = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").ClarificationCommentDto[]} */
    let clarificationComments = [];
    /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementDto[]} */
    let tentativeAgreements = [];

    // 1. Prepare wpDto
    for (const wpTabId in this.data.workPerformedClient) {
      /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedDto[]} */
      let locationWorkPerformed = [];
      /** @type {import("../../_typedefs/adrV3TypeDefs").LaborWorkPerformedDto[]} */
      let laborWorkPerformed = [];
      /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentWorkPerformedDto[]} */
      let equipmentWorkPerformed = [];

      // For locWp, first map all non-subLocWp locWpClients to locWpDtos (but including
      // any nested subLocWp's), then for each special subLocWp flattened into locWpClient,
      // find the parent locWp to update. Below is the first step:
      locationWorkPerformed = this.data.workPerformedClient[wpTabId].locationWorkPerformed
        ? this.data.workPerformedClient[wpTabId].locationWorkPerformed
            // Filtering by !isSublocation is sufficient to get all non-special subLocWp's
            // (along with regular locWp's), since it is only true for special subLocWp's.
            // NOTE: Do not filter by subLocWpType, since when !isSublocation,
            // subLocWpType can be null.
            .filter((locWpClient) => !locWpClient.isSublocation)
            .map((locWpClient) => ({
              workPerformedId: locWpClient.workPerformedId,
              locationId: locWpClient.locationId,
              workDescription: locWpClient.workDescription,
              workDescriptionDraftState: locWpClient.workDescriptionDraftState,
              inspectionType: locWpClient.inspectionType,
              quantity: locWpClient.quantity || 0,
              isControllingOperation: locWpClient.isControllingOperation,
              isDisputed: locWpClient.isDisputed,
              // Attachments, miscMaterials and testsPerformed are DTOs - no
              // separate client shape exists.
              locationWorkPerformedAttachments: locWpClient.attachments
                ? locWpClient.attachments.map((la) => ({
                    ...la,
                    createdOn: moment(la.createdOn).format(),
                  }))
                : [],
              locationMiscMaterials: locWpClient.miscMaterials
                ? locWpClient.miscMaterials.map((mm) => ({
                    ...mm,
                    quantity: mm.quantity || 0,
                  }))
                : [],
              locationTestsPerformed: locWpClient.testsPerformed
                ? locWpClient.testsPerformed.map((tp) => ({
                    ...tp,
                    numberOfTestsOrSamplesTaken: tp.numberOfTestsOrSamplesTaken || 0,
                  }))
                : [],
              sublocationWorkPerformed: locWpClient.nestedSublocations
                ? locWpClient.nestedSublocations.map((subLocWpClient) => ({
                    sublocationWorkPerformedId: subLocWpClient.sublocationWorkPerformedId,
                    locationWorkPerformedWorkPerformedId: locWpClient.workPerformedId,
                    locationWorkPerformedLocationId: locWpClient.locationId,
                    // sublocationId must be provided in this case, since it is a
                    // named, pre-defined sublocation.
                    sublocationId: subLocWpClient.sublocationId,
                    sublocationWorkPerformedType:
                      SUBLOCATION_WORKPERFORMED_TYPE.NESTEDLOCATION,
                    workDescription: subLocWpClient.workDescription,
                    workDescriptionDraftState: subLocWpClient.workDescriptionDraftState,
                    inspectionType: subLocWpClient.inspectionType,
                    quantity: subLocWpClient.quantity || 0,
                    isControllingOperation: subLocWpClient.isControllingOperation,
                    isDisputed: subLocWpClient.isDisputed,
                    // Nested sublocations do not need station/coordinate fields,
                    // since they are pre-defined.
                    sublocationMiscMaterials: subLocWpClient.miscMaterials
                      ? subLocWpClient.miscMaterials.map((subLocMm) => ({
                          ...subLocMm,
                          quantity: subLocMm.quantity || 0,
                        }))
                      : [],
                    sublocationTestsPerformed: subLocWpClient.testsPerformed
                      ? subLocWpClient.testsPerformed.map((subLocTp) => ({
                          ...subLocTp,
                          numberOfTestsOrSamplesTaken:
                            subLocTp.numberOfTestsOrSamplesTaken || 0,
                        }))
                      : [],
                    sublocationAttachments: subLocWpClient.attachments
                      ? subLocWpClient.attachments.map((sla) => ({
                          ...sla,
                          createdOn: moment(sla.createdOn).format(),
                        }))
                      : [],
                  }))
                : [],
            }))
        : [];

      // Then the second step for handling special subLocWps:
      // First collect all special subLocWps, then create a unique set of all locWp.sublocationId's.
      // (locWp.sublocationId represents locationId for special subLocWps.) A unique set is
      // needed since multiple locWprs can exist for a given location (e.g. multiple locWprs
      // each with different station ranges for a given alignment).
      // Then loop thru the set, adding matching locWp under the parent locWp.
      /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} */
      const specialSubLocWps = this.data.workPerformedClient[wpTabId]
        .locationWorkPerformed
        ? this.data.workPerformedClient[wpTabId].locationWorkPerformed.filter(
            (locWp) =>
              // locWp.isSublocation is sufficient for this filter (see comment in prev step)
              locWp.isSublocation
          )
        : [];

      // Create a unique set of all locWp.sublocationId's (which are really
      // locationId's...)
      const specialSubLocIds = Array.from(
        new Set(specialSubLocWps.map((subLocWp) => subLocWp.sublocationId))
      );
      // Loop thru the set, grouping the special subLocWps and nesting them under
      // a parent locWp
      specialSubLocIds.forEach((locationId) => {
        // Filter specialSubLocWps by locationId (i.e. group subLocWps by the subLocid):
        const groupedSpecialSubLocWps = specialSubLocWps.filter(
          (locWpClient) => locWpClient.sublocationId === locationId
        );

        // Add subLocWps under this parentLocWpClient. Mapping is from
        // LocationWorkPerformedClient type to SublocationWorkPerformedDto.
        /** @type {import("../../_typedefs/adrV3TypeDefs").SublocationWorkPerformedDto[]} */
        const subLocWps = groupedSpecialSubLocWps.map((locWpClient) => ({
          sublocationWorkPerformedId: locWpClient.sublocationWorkPerformedId,
          locationWorkPerformedWorkPerformedId: locWpClient.workPerformedId,
          locationWorkPerformedLocationId: locationId,
          sublocationId: null, // No sublocationId is used for special subLocWp
          // subLocWpType is one of the special types (alignment/coordinates/etc.)
          sublocationWorkPerformedType: locWpClient.sublocationWorkPerformedType,
          // Since this is a special subLocWp, we need all the station/coordinate data
          stationFromOrAt: locWpClient.stationFromOrAt,
          stationFromOrAtOffsetValue: locWpClient.stationFromOrAtOffsetValue,
          stationFromOrAtOffsetDirection: locWpClient.stationFromOrAtOffsetDirection,
          stationTo: locWpClient.stationTo,
          stationToOffsetValue: locWpClient.stationToOffsetValue,
          stationToOffsetDirection: locWpClient.stationToOffsetDirection,
          workDescription: locWpClient.workDescription,
          workDescriptionDraftState: locWpClient.workDescriptionDraftState,
          inspectionType: locWpClient.inspectionType,
          quantity: locWpClient.quantity || 0,
          isControllingOperation: locWpClient.isControllingOperation,
          isDisputed: locWpClient.isDisputed,
          sublocationMiscMaterials: locWpClient.miscMaterials
            ? locWpClient.miscMaterials.map((locMm) => ({
                sublocationMiscMaterialsId: locMm.sublocationMiscMaterialsId,
                sublocationWorkPerformedId: locMm.sublocationWorkPerformedId,
                materialDescription: locMm.materialDescription,
                vendorName: locMm.vendorName,
                invoiceNo: locMm.invoiceNo,
                quantity: locMm.quantity || 0,
                miscMaterialsUnitOfMeasureId: locMm.miscMaterialsUnitOfMeasureId,
              }))
            : [],
          sublocationTestsPerformed: locWpClient.testsPerformed
            ? locWpClient.testsPerformed.map((locTp) => ({
                sublocationTestPerformedId: locTp.sublocationTestPerformedId,
                sublocationWorkPerformedId: locTp.sublocationWorkPerformedId,
                fieldTestTypeId: locTp.fieldTestTypeId,
                numberOfTestsOrSamplesTaken: locTp.numberOfTestsOrSamplesTaken || 0,
                remarks: locTp.remarks,
              }))
            : [],
          sublocationAttachments: locWpClient.attachments
            ? locWpClient.attachments.map((locWpa) => ({
                sublocationAttachmentId: locWpa.sublocationAttachmentId,
                sublocationWorkPerformedId: locWpa.sublocationWorkPerformedId,
                attachmentType: locWpa.attachmentType,
                materialLotNumber: locWpa.materialLotNumber,
                description: locWpa.description,
                originalFileName: locWpa.originalFileName,
                filePath: locWpa.filePath,
                imageThumbnailFilePath: locWpa.imageThumbnailFilePath,
                createdOn: moment(locWpa.createdOn).format(),
                isDeleted: locWpa.isDeleted,
              }))
            : [],
        }));

        // Create parentLocWpDto, set subLocWp as the above-created array.
        /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedDto} */
        const parentLocWpDto = {
          workPerformedId: this.data.workPerformedClient[wpTabId].workPerformedId,
          locationId,
          workDescription: "", // Skip this for parent locWpDto
          workDescriptionDraftState: "", // Skip this for parent locWpDto
          inspectionType: 0, // Skip this for parent locWpDto
          quantity: 0, // Skip this for parent locWpDto
          isControllingOperation: subLocWps.some((slwp) => slwp.isControllingOperation),
          isDisputed: subLocWps.some((slwp) => slwp.isDisputed),
          locationMiscMaterials: [], // Skip this for parent locWpDto
          locationTestsPerformed: [], // Skip this for parent locWpDto
          locationWorkPerformedAttachments: [], // Skip this for parent locWpDto
          sublocationWorkPerformed: subLocWps,
        };
        locationWorkPerformed = [...locationWorkPerformed, parentLocWpDto];
      });

      // Map laborSetClient's laborHours into laborWorkPerformed
      Object.values(this.data.laborSetClient).forEach((lsrClient) => {
        // Note: must check for tradeId, since it CAN be null (e.g. when a duplicate
        // labor is selected without a trade.). Since tradeId forms the composite primary key
        // for laborWorkPerformed, it being null/0 will cause an error on db insert.
        if (lsrClient.tradeId && lsrClient.laborHours[wpTabId]) {
          /** @type {import("../../_typedefs/adrV3TypeDefs").LaborWorkPerformedDto} */
          const lwp = {
            // BE will ignore the entire labWp with any 0/null FK's
            workPerformedId: this.data.workPerformedClient[wpTabId].workPerformedId,
            laborId: lsrClient.laborId,
            tradeId: lsrClient.tradeId,
            regHours: lsrClient.laborHours[wpTabId].regHours,
            otHours: lsrClient.laborHours[wpTabId].otHours,
          };
          laborWorkPerformed.push(lwp);
        }
      });

      Object.values(this.data.equipmentSetClient).forEach((esr) => {
        if (esr.equipmentHours[wpTabId]) {
          /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentWorkPerformedDto} */
          const ewp = {
            workPerformedId: this.data.workPerformedClient[wpTabId].workPerformedId,
            equipmentId: esr.equipmentId,
            regHours: esr.equipmentHours[wpTabId].regHours,
            otHours: esr.equipmentHours[wpTabId].otHours,
            operatorIds: esr.equipmentHours[wpTabId].operatorIds || [],
          };
          equipmentWorkPerformed.push(ewp);
        }
      });

      /** @type {import("../../_typedefs/adrV3TypeDefs").WorkPerformedDto} */
      const wp = {
        workPerformedId: this.data.workPerformedClient[wpTabId].workPerformedId,
        assistantDailyReportId:
          this.data.workPerformedClient[wpTabId].assistantDailyReportId,
        itemId: this.data.workPerformedClient[wpTabId].itemId,
        ccoId: this.data.workPerformedClient[wpTabId].ccoId,
        pendingIssueId: this.data.workPerformedClient[wpTabId].pendingIssueId,
        isIdle: this.data.workPerformedClient[wpTabId].isIdle,
        clientId: wpTabId,
        locationWorkPerformed,
        laborWorkPerformed,
        equipmentWorkPerformed,
      };

      workPerformed.push(wp);
    }

    // 2. Prepare laborSetDto and laborRemarks
    Object.values(this.data.laborSetClient).forEach((lsrClient) => {
      laborSet.push({
        laborId: lsrClient.laborId,
        tradeId: lsrClient.tradeId,
        rowOrder: lsrClient.rowOrder,
      });

      laborRemarks.push({
        laborRemarksId: lsrClient.remarks.laborRemarksId,
        assistantDailyReportId: this.data.assistantDailyReportId,
        laborId: lsrClient.laborId,
        tradeId: lsrClient.tradeId,
        remarks: lsrClient.remarks.remarks,
      });
    });

    // 3. Prepare equipmentSetDto and equipmentRemarks
    Object.values(this.data.equipmentSetClient).forEach((esrClient) => {
      equipmentSet.push({
        equipmentId: esrClient.equipmentId,
        rowOrder: esrClient.rowOrder,
      });

      equipmentRemarks.push({
        equipmentRemarksId: esrClient.remarks.equipmentRemarksId,
        assistantDailyReportId: this.data.assistantDailyReportId,
        equipmentId: esrClient.equipmentId,
        remarks: esrClient.remarks.remarks,
      });
    });

    // 4. Prepare generalComments
    this.data.generalComments.forEach((gcClient) => {
      // Add if EITHER subject or narrative is not blank
      if (gcClient.subject || gcClient.narrative) {
        generalComments.push({
          ...gcClient,
          commentAttachments: [...gcClient.commentAttachments],
        });
      }
    });

    this.data.correctionComments.forEach((cc) => {
      correctionComments.push({
        ...cc,
        commentDate: moment(cc.commentDate).format(),
        fieldInfo: stringifyJSON(cc.fieldInfo),
      });
    });

    this.data.clarificationComments.forEach((cc) => {
      if (cc.subject || cc.comment)
        clarificationComments.push({
          ...cc,
          commentDate: moment(cc.commentDate).format(),
          keywordIds: [...cc.keywordIds],
        });
    });

    this.data.tentativeAgreements.forEach((ta) => {
      let taLocations = "";
      try {
        taLocations = JSON.stringify(Object.values(ta.locations || {}));
      } catch (e) {
        console.error("Failed JSON.stringify():", e);
        taLocations = "";
      }
      tentativeAgreements.push({
        ...ta,
        locations: taLocations,
        createdOn: moment(ta.createdOn).format(),
        caltransRepSignedOn: ta.caltransRepSignedOn
          ? moment(ta.caltransRepSignedOn).format()
          : null,
        contractors: ta.contractorsClient
          ? Object.values(ta.contractorsClient).map((tac) => ({
              ...tac,
              contractorRepSignedOn: tac.contractorRepSignedOn
                ? moment(tac.contractorRepSignedOn).format()
                : null,
            }))
          : [],
        labors: ta.laborsClient ? Object.values(ta.laborsClient) : [],
        equipment: ta.equipmentClient ? Object.values(ta.equipmentClient) : [],
        materials: ta.materialsClient ? Object.values(ta.materialsClient).flat() : [],
        attachments: ta.attachments ? ta.attachments.map((taa) => ({ ...taa })) : [],
      });
    });

    adrDto = {
      ...this.data,
      // Preprocess time strings. Do not rely on default JSON conversion since
      // it will produce ISO time string in UTC only.
      // moment(date).format() outputs ISO 8601 time string WITH timezone offset.
      reportStartDate: moment(this.data.reportStartDate).format(),
      reportEndDate: moment(this.data.reportEndDate).format(),
      createdOn: moment(this.data.createdOn).format(),
      draftSavedOn: this.data.draftSavedOn
        ? moment(this.data.draftSavedOn).format()
        : null,
      submittedOn: this.data.submittedOn ? moment(this.data.submittedOn).format() : null,
      approvedOn: this.data.approvedOn ? moment(this.data.approvedOn).format() : null,
      rejectedOn: this.data.rejectedOn ? moment(this.data.rejectedOn).format() : null,
      supersededOn: this.data.supersededOn
        ? moment(this.data.supersededOn).format()
        : null,
      // Preprocess weatherCondition. If -1, set as null
      weatherCondition:
        this.data.weatherCondition < 0 ? null : this.data.weatherCondition,
      workPerformed,
      laborSet,
      equipmentSet,
      laborRemarks,
      equipmentRemarks,
      generalComments, // Overwrites existing generalComments property
      correctionComments, // Overwrites existing correctionComments property
      clarificationComments, // Overwrites existing clarificationComments property
      tentativeAgreements,
    };

    // workPerformedClient was copied over in the above operation (...this.data),
    // but is not needed for DTO.
    delete adrDto.workPerformedClient;
    delete adrDto.laborSetClient;
    delete adrDto.equipmentSetClient;

    // console.log("adrDto out=", adrDto);

    return adrDto;
  };

  /**
   * Maps current client state into Cem4601PdfDto.
   * @returns {AssistantDailyReportPdf}
   * @param {Boolean} isPreviewOnly
   */
  this.mapClientToCem4601PdfDto = (isPreviewOnly) => {
    const header = {
      writtenBy: this.data.authorFullName,
      reviewedBy: "",
      reportNo: this.data.reportNo,
      reportNoSuffix: this.data.reportNoSuffix,
      reportStartDate: moment(this.data.reportStartDate).format(),
      reportEndDate: moment(this.data.reportEndDate).format(),
      customCategory: this.data.customCategory,
      contractorStartShift: this.data.contractorStartShift, // string
      contractorEndShift: this.data.contractorEndShift, // string
      engineerStartShift: this.data.engineerStartShift, //string,
      engineerEndShift: this.data.engineerEndShift, // string,
      engineerOT: this.data.engineerOT,
      engineerOTReason: this.data.engineerOTReason,
      engineerOnLeave: this.data.engineerOnLeave,
      weatherCondition: this.data.weatherCondition,
      highTemp: this.data.highTemp,
      lowTemp: this.data.lowTemp,
      weatherComment: this.data.weatherComment,
      nonWorkingDay: this.data.nonWorkingDay,
    };

    let workPerformedPdf = [];
    // Modify workPerformedClient
    for (const wpTabId in this.data.workPerformedClient) {
      const wp = this.data.workPerformedClient[wpTabId];
      const locWpPdf = wp.locationWorkPerformed.map((locWp) => ({
        workPerformedId: wp.workPerformedId,
        locationId: locWp.locationId,
        sublocationId: locWp.sublocationId,
        inspectionType: locWp.inspectionType,
        workDescription: locWp.workDescription,
        workDescriptionDraftState: locWp.workDescriptionDraftState,
        quantity: locWp.quantity || 0,
        isSublocation: locWp.isSublocation,
        sublocationWorkPerformedId: locWp.sublocationWorkPerformedId,
        sublocationWorkPerformedType: locWp.sublocationWorkPerformedType,
        stationFromOrAt: locWp.stationFromOrAt,
        stationFromOrAtOffsetValue: locWp.stationFromOrAtOffsetValue,
        stationFromOrAtOffsetDirection: locWp.stationToOffsetDirection,
        stationTo: locWp.stationTo,
        stationToOffsetValue: locWp.stationToOffsetValue,
        stationToOffsetDirection: locWp.stationToOffsetDirection,
        miscMaterials: locWp.miscMaterials.map((locMm) => ({ ...locMm })),
        testsPerformed: locWp.testsPerformed.map((locTp) => ({ ...locTp })),
        attachments: locWp.attachments
          .filter((locWpa) => !locWpa.isDeleted)
          .map((locWpa) => ({ ...locWpa })),
      }));

      workPerformedPdf.push({
        workPerformedId: wp.workPerformedId,
        bidItemId: wp.itemId,
        ccoId: wp.ccoId,
        pendingIssueId: wp.pendingIssueId,
        isIdle: wp.isIdle,
        sortOrder: wp.sortOrder,
        locationWorkPerformed: locWpPdf,
      });
    }

    let laborSetPdf = [];
    for (const rowKey in this.data.laborSetClient) {
      // this.data.laborSetClient[rowKey] is LSR
      const lsr = this.data.laborSetClient[rowKey];
      let laborHours = {};
      for (let wpTabId in lsr.laborHours) {
        const wpId = this.data.workPerformedClient[wpTabId].workPerformedId;
        laborHours[wpId] = { ...lsr.laborHours[wpTabId], workPerformedId: wpId };
      }
      laborSetPdf.push({ ...lsr, laborHours, remarks: lsr.remarks.remarks });
    }

    let equipmentSetPdf = [];
    for (const rowKey in this.data.equipmentSetClient) {
      const esr = this.data.equipmentSetClient[rowKey];
      let equipmentHours = {};
      for (const wpTabId in esr.equipmentHours) {
        const wpId = this.data.workPerformedClient[wpTabId].workPerformedId;
        equipmentHours[wpId] = {
          ...esr.equipmentHours[wpTabId],
          workPerformedId: wpId,
        };
      }
      equipmentSetPdf.push({ ...esr, equipmentHours, remarks: esr.remarks.remarks });
      // MULTIPLE OPERATORS: FOR FUTURE USE
      // // Although esr.operators is not nested under esr.equipmentHours,
      // // we can still operate on esr.operators[wpTabId] since we're looping
      // // thru wpTabIds already, for esr.equipmentHours.
      // // Note: esr.operators cannot exist if !esr.equipmentHours.
      // if (esr.operators && esr.operators[wpTabId]) esr.operators[wpTabId] = [];
    }

    const genCommentsPdf = this.data.generalComments
      .filter((gc) => !(!gc.subject && !gc.narrative && !gc.keywordIds.length === 0))
      .map((gc) => ({
        commenntId: gc.commentId,
        keywordIds: gc.keywordIds.map((kwId) => kwId),
        subject: gc.subject,
        narrative: gc.narrative,
        narrativeDraftState: gc.narrativeDraftState,
        attachments: gc.commentAttachments
          .filter((gca) => !gca.isDeleted)
          .map((gca) => ({ ...gca })),
      }));

    const adrPdf = {
      assistantDailyReportId: this.data.assistantDailyReportId,
      projectId: this.data.projectId,
      isPreviewOnly,
      isSuperseded: this.data.isSuperseded,
      isDraft: this.data.isDraft,
      isSubmitted: this.data.isSubmitted,
      isApproved: this.data.isApproved,
      isRejected: this.data.isRejected,
      header,
      workPerformed: workPerformedPdf,
      laborSet: laborSetPdf,
      equipmentSet: equipmentSetPdf,
      generalComments: genCommentsPdf,
    };

    return adrPdf;
  };

  /**
   * Modifies this.data based on the copy rules. It does not return a clone of adrClient
   * since this.data is already a deep clone of the provided adrClient.
   */
  this.copyClient = () => {
    // Reset all status flags and header data
    const now = new Date();
    this.data.assistantDailyReportId = 0;
    this.data.isCopy = true;
    this.data.isDraft = true;
    this.data.isSubmitted = false;
    this.data.isApproved = false;
    this.data.isRejected = false;
    this.data.isSuperseded = false;
    this.data.isAmendment = false;
    this.data.draftSavedOn = null;
    this.data.submittedOn = null;
    this.data.approvedOn = null;
    this.data.rejectedOn = null;
    this.data.supersededOn = null;
    this.data.amendmentNo = 0;
    this.data.amendedAssistantDailyReportId = null;
    this.data.originalAssistantDailyReportId = null;
    this.data.createdOn = now;
    this.data.reportNo = "";
    this.data.reportNoSuffix = "";
    this.data.authorUserId = 0; // AdrMainV3 overrides with current userId in CDM
    this.data.authorEmployeeNo = ""; // AdrMainV3 overrides with current userId in CDM
    this.data.authorFirstName = ""; // AdrMainV3 overrides with current userId in CDM
    this.data.authorLastName = ""; // AdrMainV3 overrides with current userId in CDM
    this.data.authorFullName = ""; // AdrMainV3 overrides with current userId in CDM
    this.data.reportStartDate = now;
    this.data.reportEndDate = now;
    this.data.contractorStartShift = "0630";
    this.data.contractorEndShift = "1500";
    this.data.engineerStartShift = "0700";
    this.data.engineerEndShift = "1530";
    this.data.engineerOT = 0;
    this.data.engineerOTReason = "";
    this.data.engineerOnLeave = false;
    this.data.weatherCondition = 0;
    this.data.highTemp = "";
    this.data.lowTemp = "";
    this.data.weatherComment = "";
    this.data.hasCozeep = false;
    this.data.reviewerUserId = null;
    this.data.reviewStartedOn = null;
    this.data.isInReview = false;
    // this.data.additionalMetadata =

    // Modify workPerformedClient
    for (const wpTabId in this.data.workPerformedClient) {
      this.data.workPerformedClient[wpTabId] = {
        // Keep the itemId/ccoId/pendingIssueId, unitOfMeasure, and
        // any captions for tab labeling
        ...this.data.workPerformedClient[wpTabId],
        // Blank out the id
        workPerformedId: 0,
        // Keep all locWp rows, but blank out all id and data within each locWp
        locationWorkPerformed: this.data.workPerformedClient[
          wpTabId
        ].locationWorkPerformed.map((locWp, index) => ({
          clientId: index + 1,
          workPerformedId: 0,
          locationId: locWp.locationId,
          workDescription: "",
          workDescriptionDraftState: "",
          inspectionType: 0,
          quantity: 0,
          isControllingOperation: false,
          isDisputed: false,
          isSublocation: locWp.isSublocation,
          sublocationWorkPerformedId: 0,
          sublocationWorkPerformedType: locWp.sublocationWorkPerformedType,
          sublocationId: locWp.isSublocation ? locWp.sublocationId : 0,
          stationFromOrAt: locWp.isSublocation ? locWp.stationFromOrAt : "",
          stationFromOrAtOffsetValue: locWp.isSublocation
            ? locWp.stationFromOrAtOffsetValue
            : "",
          stationFromOrAtOffsetDirection: locWp.isSublocation
            ? locWp.stationFromOrAtOffsetDirection
            : "",
          stationTo: locWp.isSublocation ? locWp.stationTo : "",
          stationToOffsetValue: locWp.isSublocation ? locWp.stationToOffsetValue : "",
          stationToOffsetDirection: locWp.isSublocation
            ? locWp.stationToOffsetDirection
            : "",
          attachments: [],
          miscMaterials: [],
          testsPerformed: [],
          nestedSublocations: locWp.nestedSublocations
            ? locWp.nestedSublocations.map((subLocWpClient) => ({
                sublocationWorkPerformedId: 0,
                sublocationId: subLocWpClient.sublocationId,
                sublocationWorkPerformedType:
                  SUBLOCATION_WORKPERFORMED_TYPE.NESTEDLOCATION,
                workDescription: "",
                workDescriptionDraftState: "",
                inspectionType: 0,
                quantity: 0,
                isControllingOperation: false,
                isDisputed: false,
                attachments: [],
                miscMaterials: [],
                testsPerformed: [],
              }))
            : [],
        })),
      };
    }

    // Modify laborSet. Keep labor & trade and rowOrder, but blank out the rest.
    for (const rowKey in this.data.laborSetClient) {
      // this.data.laborSetClient[rowKey] is LSR
      const laborHours = this.data.laborSetClient[rowKey].laborHours || null;
      if (laborHours) {
        for (const wpTabId in laborHours) {
          laborHours[wpTabId].regHours = null;
          laborHours[wpTabId].otHours = null;
        }
      }

      this.data.laborSetClient[rowKey].remarks = { laborRemarksId: 0, remarks: "" };
    }

    // Modify equipmentSet.
    for (const rowKey in this.data.equipmentSetClient) {
      const equipmentHours = this.data.equipmentSetClient[rowKey].equipmentHours || null;
      if (equipmentHours) {
        for (const wpTabId in equipmentHours) {
          equipmentHours[wpTabId].regHours = null;
          equipmentHours[wpTabId].otHours = null;
          // Keep operatorIds as-is; no need to reset.
        }
      }

      this.data.equipmentSetClient[rowKey].remarks = {
        equipmentRemarksId: 0,
        remarks: "",
      };
    }

    // Clear all comments.
    this.data.generalComments = [];
    this.data.clarificationComments = [];
    this.data.correctionComments = [];

    // Clear all tentativeAgreements.
    this.data.tentativeAgreements = [];
  };

  this.setId = (newId) => (this.data.assistantDailyReportId = newId);
  this.setIsCopy = (isCopy) => (this.data.isCopy = isCopy);
}

/****************************************************************************************
 * Provides methods operating on WorkPerformedClient (which is WorkPerformed[]
 * normalized by wpTabId.)
 * @param {Object.<number, WorkPerformedClient>} wpClient - normalized by wpTabId
 * @param {?number} wpTabIdInput - allows client to specify if only a specific Wp should
 *                                 be deep-cloned
 ***************************************************************************************/
export function WorkPerformedFactory(wpClient, wpTabIdInput = null) {
  if (!wpClient || typeof wpClient !== "object") wpClient = {};

  /** @type {Object.<number, import("../../_typedefs/adrV3TypeDefs").WorkPerformedClient>} */
  this.data = {}; // Create a blank container for wp data
  for (const wpTabId in wpClient) {
    // If a wpTabId is specified at input (wpTabIdInput), deep clone only the matching Wp.
    // (This is to prevent unnecessarily deep cloning all Wp's when only one is being
    // updated.) It does NOT clone locationWorkPerformed.
    if (wpTabIdInput)
      this.data[wpTabId] =
        +wpTabId === wpTabIdInput ? { ...wpClient[wpTabId] } : wpClient[wpTabId];
    else this.data[wpTabId] = { ...wpClient[wpTabId] };
  }

  /**
   * Adds new workPerformedClient
   * @param {import("../../_typedefs/adrV3TypeDefs").WorkPerformedClient} newWp
   *        - new workPerformedClient
   */
  this.add = (newWp) => {
    if (!newWp || typeof newWp !== "object") return;

    const wpTabIds = this.getAllTabIds();
    if (wpTabIds.length === MAX_COLUMNS) return;

    const wpSortOrder = this.getSortOrder();

    const wpTabId = wpTabIds.length > 0 ? Math.max(...wpTabIds) + 1 : 1; // wpTabId should never be 0
    const sortOrder =
      wpSortOrder.length > 0 ? Math.max(...wpSortOrder.map((so) => so.sortOrder)) + 1 : 0;
    this.data[wpTabId] = { wpTabId, sortOrder, ...newWp };
    this.newWpTabId = wpTabId;
  };

  this.delete = (wpTabId) => {
    if (!wpTabId) return;
    // "Orphaned" locWpa's will be collected by BE for deletion, no need to do anything here
    delete this.data[wpTabId];
    this.resetSortOrder();
  };

  /**
   * Updates workPerformedClient[wpTabId].locationWorkPerformed, when updates occur.
   * This is necessary since updates are done on a copy of locWp - the new locWp must
   * replace the existing within workPerformedClient[wpTabId].
   * @param {number} wpTabId
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} locWps
   *        - entire locWp to replace existing with.
   */
  this.updateLocationWorkPerformed = (wpTabId, locWps) => {
    if (!locWps || !Array.isArray(locWps)) return;
    this.data[wpTabId].locationWorkPerformed = locWps;
  };

  this.move = (fromWpTabId, toWpTabId) => {
    const newPos = this.data[toWpTabId].sortOrder;
    this.data[fromWpTabId].sortOrder = newPos;
  };

  this.getAllTabIds = () => Object.keys(this.data).map((t) => +t);

  /**
   * Returns wpSortOrder. Any UI-based need for wpSortOrder should use
   * adrActions.getWorkPerformedSortOrder instead of this, to prevent unnecessary
   * deep-copying of workPerformedClient.
   * @returns {import("../../_typedefs/adrV3TypeDefs").WpSortOrder[]}
   */
  this.getSortOrder = () =>
    _.orderBy(
      Object.entries(this.data)
        .filter(([key, value]) => key !== "newWpTabId" || typeof value !== "object")
        .map(([wpTabId, wp]) => ({
          wpTabId: +wpTabId,
          sortOrder: +wp.sortOrder,
        })),
      "sortOrder",
      "asc"
    );

  this.resetSortOrder = () => {
    const sortOrder = this.getSortOrder();
    sortOrder.forEach((so, i) => (this.data[so.wpTabId].sortOrder = i));
  };
}

/****************************************************************************************
 * Provides methods operating on locWp data, including its nested lists (attachments,
 * misc materials and tests performed.)
 * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} locWps
 *        - Array of locWp's
 * @param {?number} clientIdToCopy - Specifies which locWpr to deep-copy.
 *                                   null for copy all.
 * @param {?boolean} doNotCopy - Specifies whether any locWprs should be deep-copied.
 *                               Client must explicitly state if NO deep copy is desired.
 *                               (i.e. default behavior is some kind of deep-copying,
 *                               whether partial or all.) Also note that if doNotCopy
 *                               is true, any value provided in clientIdToCopy will be
 *                               ignored.
 ***************************************************************************************/
export function LocationWorkPerformedFactory(
  locWps,
  clientIdToCopy = null,
  doNotCopy = false
) {
  if (!locWps || !Array.isArray(locWps)) locWps = [];

  // clientIdToCopy can be null, 0 or a positive #, but never negative.
  // Setting to 0 will trigger the default behavior of deep-copying none.
  if (!isNaN(clientIdToCopy) && clientIdToCopy < 0) clientIdToCopy = 0;

  // If doNotCopy is not provided, default is false.
  if (!doNotCopy) {
    // If clientIdToCopy is null, copy all.
    if (clientIdToCopy === null) {
      /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient[]} */
      this.data = locWps.map((locWpc) => ({
        ...locWpc,
        attachments: locWpc.attachments.map((locWpa) => ({ ...locWpa })),
        miscMaterials: locWpc.miscMaterials.map((locMm) => ({ ...locMm })),
        testsPerformed: locWpc.testsPerformed.map((locTp) => ({ ...locTp })),
        nestedSublocations: locWpc.nestedSublocations.map((ns) => ({
          ...ns,
          miscMaterials: ns.miscMaterials.map((nsMm) => ({ ...nsMm })),
          testsPerformed: ns.testsPerformed.map((nsTp) => ({ ...nsTp })),
          attachments: ns.attachments.map((nsa) => ({ ...nsa })),
        })),
      }));
    } else {
      // If clientIdToCopy provided, deep copy locWpc with matching clientId.
      // If not, keep the object reference.
      this.data = [];
      for (let i = 0; i < locWps.length; i++) {
        if (locWps[i].clientId !== clientIdToCopy)
          // Keep the object reference
          this.data = [...this.data, locWps[i]];
        // Deep copy
        else {
          this.data = [
            ...this.data,
            {
              ...locWps[i],
              attachments: locWps[i].attachments.map((a) => ({ ...a })),
              miscMaterials: locWps[i].miscMaterials.map((mm) => ({ ...mm })),
              testsPerformed: locWps[i].testsPerformed.map((tp) => ({ ...tp })),
              nestedSublocations: locWps[i].nestedSublocations.map((ns) => ({
                ...ns,
                miscMaterials: ns.miscMaterials.map((nsMm) => ({ ...nsMm })),
                testsPerformed: ns.testsPerformed.map((nsTp) => ({ ...nsTp })),
                attachments: ns.attachments.map((nsa) => ({ ...nsa })),
              })),
            },
          ];
        }
      }
    }
  } else {
    this.data = locWps;
  }

  /**
   * Add new LocationWorkPerformed row
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} newLoc
   *        - Blank locWp object
   */
  this.add = (newLoc) => {
    const nextClientId =
      (this.data.length > 0
        ? Math.max(...this.data.map((locWpc) => locWpc.clientId))
        : 0) + 1;
    if (!newLoc || typeof newLoc !== "object")
      newLoc = {
        clientId: nextClientId,
        locationId: 0,
        workPerformedId: 0,
        workDescription: "",
        workDescriptionDraftState: "",
        inspectionType: 0,
        quantity: 0,
        isControllingOperation: false,
        isDisputed: false,
        isSublocation: false,
        sublocationWorkPerformedId: 0,
        sublocationWorkPerformedType: null,
        sublocationId: 0,
        stationFromOrAt: "",
        stationFromOrAtOffsetValue: "",
        stationFromOrAtOffsetDirection: "",
        stationTo: "",
        stationToOffsetValue: "",
        stationToOffsetDirection: "",
        attachments: [],
        miscMaterials: [],
        testsPerformed: [],
        nestedSublocations: [],
      };
    newLoc = { ...newLoc, clientId: nextClientId };
    this.data = [...this.data, newLoc];
  };

  /**
   * Delete LocationWorkPerformed row
   * @param {number} clientId
   */
  this.delete = (clientId) => {
    if (clientId == null || this.data.length === 0) return;
    const locWpcIndex = this.data.findIndex((locWpc) => locWpc.clientId === clientId);
    if (locWpcIndex < 0) return;
    this.data = [...this.data.slice(0, locWpcIndex), ...this.data.slice(locWpcIndex + 1)];
  };

  /**
   * Update LocationWorkPerformed data, excluding nested lists
   * (i.e. attachments, misc materials, and tests performed).
   * @param {number} clientId - locWpr index
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedClient} fields
   *        - LocationWorkPerformed fields to update
   */
  this.update = (clientId, fields) => {
    if (clientId == null || typeof fields !== "object") return;

    const locWpcToUpdate = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpcToUpdate) return;

    for (const field in fields) {
      locWpcToUpdate[field] = fields[field];
    }
  };

  this.reorder = (currRowIndex, targetRowIndex) => {
    if (currRowIndex == null || targetRowIndex == null) return;

    const targetRowOrder = this.data[targetRowIndex].rowOrder;
    this.data.forEach((locWpc) => locWpc.rowOrder >= targetRowOrder && locWpc.rowOrder++);
    this.data[currRowIndex].rowOrder = targetRowOrder;
  };

  /**
   * Provides sort order of LocationWorkPerformed rows, using locationId as key.
   * Client needs to ensure that LocationWorkPerformed is a unique set of
   * locationId's.
   */
  this.getSortOrder = () =>
    this.data.length > 0
      ? _.orderBy(
          this.data.map((locWp) => ({
            locationId: locWp.locationId,
            sortOrder: locWp.sortOrder,
          })),
          "sortOrder",
          "asc"
        )
      : [];

  /**
   * Add metadata for new files to be added
   * @param {number} clientId - locWpr index
   * @param {number} wpId - workPerformedId (for FK)
   * @param {import("../../_typedefs/adrV3TypeDefs").UploadedFiles} uploadedFiles
   */
  this.addAttachment = (clientId, wpId, uploadedFiles) => {
    if (clientId == null) return;
    if (!uploadedFiles || typeof uploadedFiles !== "object") return;

    let locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    // Add attachment to parent-level locWpr, including special sublocations.
    // Do NOT handle nested sublocation attachments here - see this.addSublocationAttachment().
    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedAttachmentsDto[]|import("../../_typedefs/adrV3TypeDefs").SublocationAttachmentDto[]} */
    let filesToAdd = [];
    const {
      filePaths,
      originalFileNames,
      imageThumbnailFilePaths,
      thumbnailBase64s,
      createdOn,
      descriptions,
    } = uploadedFiles;
    filePaths.forEach((filePath, index) => {
      filesToAdd.push({
        // Add properties specific to either locWpa or subLocWpa conditionally based
        // on locWp.isSublocation:
        ...(locWpc.isSublocation
          ? {
              sublocationAttachmentId: 0,
              sublocationWorkPerformedId: locWpc.sublocationWorkPerformedId,
            }
          : {
              locationWorkPerformedAttachmentId: 0,
              locationWorkPerformedWorkPerformedId: wpId,
              locationWorkPerformedLocationId: locWpc.locationId,
            }),
        attachmentType: 0, // Default is set to Photo (General). User can change.
        originalFileName: originalFileNames[index],
        filePath,
        description: descriptions?.[index] ?? "",
        materialLotNumber: "",
        imageThumbnailFilePath: imageThumbnailFilePaths[index],
        thumbnailBase64: thumbnailBase64s[index],
        createdOn,
        isDeleted: false,
      });
    });

    locWpc.attachments = [...locWpc.attachments, ...filesToAdd];
  };

  /**
   *
   * @param {number} clientId - locWpr index
   * @param {number} fileRowIndex - fileRowIndex
   */
  this.toggleDeleteAttachment = (clientId, fileRowIndex) => {
    if (!clientId == null || !fileRowIndex == null) return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    // Attachment deletion is handled via .isDeleted flag
    locWpc.attachments[fileRowIndex].isDeleted =
      !locWpc.attachments[fileRowIndex].isDeleted;
  };

  /**
   * Updates locWpa metadata
   * @param {number} clientId
   * @param {number} fileRowIndex
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationWorkPerformedAttachmentsDto} attachmentFields
   */
  this.updateAttachment = (clientId, fileRowIndex, attachmentFields) => {
    if (clientId == null || fileRowIndex == null) return;
    if (!attachmentFields || typeof attachmentFields !== "object") return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    for (const attachmentField in attachmentFields) {
      locWpc.attachments[fileRowIndex][attachmentField] =
        attachmentFields[attachmentField];
    }
  };

  /**
   * Add new MiscMaterials
   * @param {number} clientId - locWpr index
   * @param {number} wpId - workPerformedId (for FK)
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationMiscMaterialsDto} locMm
   */
  this.addMiscMaterials = (clientId, wpId) => {
    if (clientId == null) return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationMiscMaterialsDto|import("../../_typedefs/adrV3TypeDefs").SublocationMiscMaterialsDto} */
    const locMm = {
      // Add properties specific to either locMm or subLocMm conditionally based
      // on locWp.isSublocation:
      ...(locWpc.isSublocation
        ? {
            sublocationMiscMaterialsId: 0,
            sublocationWorkPerformedId: locWpc.sublocationWorkPerformedId,
          }
        : {
            locationMiscMaterialsId: 0,
            locationWorkPerformedLocationId: locWpc.locationId,
            locationWorkPerformedWorkPerformedId: wpId,
          }),
      materialDescription: "",
      quantity: 0,
      miscMaterialsUnitOfMeasureId: 0,
    };

    locWpc.miscMaterials = [...locWpc.miscMaterials, { ...locMm }];
  };

  this.deleteMiscMaterials = (clientId, mmIndex) => {
    if (clientId == null || mmIndex == null) return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    locWpc.miscMaterials = [
      ...locWpc.miscMaterials.slice(0, mmIndex),
      ...locWpc.miscMaterials.slice(mmIndex + 1),
    ];
  };

  /**
   * Update locMm
   * @param {number} clientId
   * @param {number} mmIndex
   * @param {import("../../_typedefs/adrV3TypeDefs").LocationMiscMaterialsDto} fields
   */
  this.updateMiscMaterials = (clientId, mmIndex, fields) => {
    if (clientId == null || mmIndex == null) return;
    if (!fields || typeof fields !== "object") return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    for (const field in fields) {
      locWpc.miscMaterials[mmIndex][field] = fields[field];
    }
  };

  this.addTestPerformed = (clientId, wpId) => {
    if (clientId == null) return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    /** @type {import("../../_typedefs/adrV3TypeDefs").LocationTestPerformedDto|import("../../_typedefs/adrV3TypeDefs").SublocationTestPerformedDto} */
    const locTp = {
      // Add properties specific to either locMm or subLocMm conditionally based
      // on locWp.isSublocation:
      ...(locWpc.isSublocation
        ? {
            sublocationTestPerformedId: 0,
            sublocationWorkPerformedId: locWpc.sublocationWorkPerformedId,
          }
        : {
            locationTestPerformedId: 0,
            locationWorkPerformedWorkPerformedId: wpId,
            locationWorkPerformedLocationId: locWpc.locationId,
          }),
      locationWorkPerformedWorkPerformedId: 0,
      fieldTestTypeId: 0,
      numberOfTestsOrSamplesTaken: "",
      remarks: "",
    };

    locWpc.testsPerformed = [...locWpc.testsPerformed, { ...locTp }];
  };

  this.deleteTestPerformed = (clientId, tpIndex) => {
    if (clientId == null || tpIndex == null) return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    locWpc.testsPerformed = [
      ...locWpc.testsPerformed.slice(0, tpIndex),
      ...locWpc.testsPerformed.slice(tpIndex + 1),
    ];
  };

  this.updateTestPerformed = (clientId, tpIndex, fields) => {
    if (clientId == null || tpIndex == null) return;
    if (!fields || typeof fields !== "object") return;

    const locWpc = this.data.find((locWpc) => locWpc.clientId === clientId);
    if (!locWpc) return;

    for (const field in fields) {
      locWpc.testsPerformed[tpIndex][field] = fields[field];
    }
  };
}

/****************************************************************************************
 * Provides methods operating on LaborSetClient.
 * LaborSetClient is a superset object of LaborSetDto representing a unique set of
 * {laborId, tradeId}, but with more client-side properties added immediately
 * upon successful fetch. The param for this constructor MUST be of type
 * LaborSetClient - see typedefs for more info.
 * LaborSetClient data must be repackaged to the shape of LaborWorkPerformed prior to
 * uploading.
 * @param {Object.<string, import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient>} laborSetClient
 *        - LaborSetClients by rowKey as keys
 ***************************************************************************************/
export function LaborSetFactory(laborSetClient, rowKeyToCopy = null, doNotCopy = false) {
  if (!laborSetClient || typeof laborSetClient !== "object") laborSetClient = {};

  // If a copy is desired:
  if (!doNotCopy) {
    // Unless a specific rowKeyToCopy is provided, copy every row
    if (rowKeyToCopy) {
      /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient>} */
      this.data = {};
      for (const rowKey in laborSetClient) {
        this.data[rowKey] =
          rowKeyToCopy === rowKey
            ? _.cloneDeep(laborSetClient[rowKey])
            : laborSetClient[rowKey];
      }
    } else {
      /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient>} */
      this.data = _.cloneDeep(laborSetClient);
    }
  } else this.data = laborSetClient;

  this.getMaxRowOrder = () =>
    Object.keys(this.data).length > 0
      ? Math.max(...Object.values(this.data).map((lsrClient) => lsrClient.rowOrder))
      : 0;

  this.getNextRowKeyValue = () => {
    const keys = Object.keys(this.data);
    return keys.length > 0
      ? Math.max(...keys.map((rowKey) => +rowKey.split("_")[1])) + 1
      : 1;
  };

  /**
   * Add new LaborSetClient row.
   * @param {number[]} allWpTabIds - List of all wpTabIds, to set each LaborHours
   * @param {number} insertAt - Row index (0-based) at which to insert the new row
   * @param {number} rowsToAdd - Number of rows to add. Defaults to 1 if not provided.
   */
  this.addBlankRows = (allWpTabIds, insertAt = null, rowsToAdd = 1) => {
    if (!allWpTabIds || !Array.isArray(allWpTabIds)) return;
    if (insertAt != null && insertAt < 0) return;

    /** @type {import("../../_typedefs/adrV3TypeDefs").LaborHours} */
    let laborHours = {};
    allWpTabIds.forEach((wpTabId) => {
      laborHours[wpTabId] = {
        wpTabId: +wpTabId,
        regHours: "",
        otHours: "",
      };
    });

    // If insertAt is provided, increment all rowOrders that are greater than insertAt.
    if (insertAt != null)
      Object.values(this.data).forEach((lsrClient) => {
        if (lsrClient.rowOrder >= insertAt) lsrClient.rowOrder += rowsToAdd;
      });

    /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient>} */
    const nextRowKeyValue = this.getNextRowKeyValue();
    const currMaxRowOrder = this.getMaxRowOrder();

    for (let i = 0; i < rowsToAdd; i++) {
      const rowKey = `lsrKey_${nextRowKeyValue + i}`;

      // Insert new row
      this.data[rowKey] = {
        rowKey,
        laborId: 0,
        contractorId: 0,
        tradeId: 0,
        laborHours,
        remarks: { laborRemarksId: 0, remarks: "" },
        rowOrder: insertAt == null ? currMaxRowOrder + 1 + i : insertAt + i,
      };
    }
  };

  /**
   * Add multiple rows with preloaded labor data
   * @param {number[]} allWpTabIds
   * @param {number} insertAt
   * @param {import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient[]} laborData
   *        - laborHours may be supplied
   */
  this.addRows = (allWpTabIds, insertAt = null, laborData) => {
    if (!allWpTabIds || !Array.isArray(allWpTabIds)) return;
    if (!laborData || !Array.isArray(laborData)) return;
    if (insertAt != null && insertAt < 0) return;

    // If insertAt is provided, increment all rowOrders that are greater than insertAt.
    if (insertAt != null) {
      Object.values(this.data).forEach((lsrClient) => {
        if (lsrClient.rowOrder >= insertAt) lsrClient.rowOrder += laborData.length;
      });
    }

    const nextRowKeyValue = this.getNextRowKeyValue();
    const currMaxRowOrder = this.getMaxRowOrder();

    // Loop thru the provided laborData
    for (let i = 0; i < laborData.length; i++) {
      const rowKey = `lsrKey_${nextRowKeyValue + i}`;

      /** @type {import("../../_typedefs/adrV3TypeDefs").LaborHours} */
      const laborHours = {}; // Initialize
      allWpTabIds.forEach((wpTabId) => {
        // Note: do not place incoming laborHours straight into the new laborSetRow.
        // Rather, check if laborHours[wpTabId] exists in laborData[i] for all known
        // wpTabIds, and copy into a known shape. (i.e. do not trust what incoming
        // laborHours might contain.)
        laborHours[wpTabId] = {
          wpTabId: +wpTabId,
          regHours:
            laborData[i].laborHours && laborData[i].laborHours[wpTabId]
              ? laborData[i].laborHours[wpTabId].regHours || ""
              : "",
          otHours:
            laborData[i].laborHours && laborData[i].laborHours[wpTabId]
              ? laborData[i].laborHours[wpTabId].otHours || ""
              : "",
        };
      });

      // Insert new row
      this.data[rowKey] = {
        rowKey,
        laborId: laborData[i].laborId,
        contractorId: laborData[i].contractorId,
        tradeId: laborData[i].tradeId,
        laborHours,
        remarks: { laborRemarksId: 0, remarks: "" },
        rowOrder: insertAt == null ? currMaxRowOrder + 1 + i : insertAt + i,
      };
    }
  };

  this.deleteRow = (rowKeyToDel) => {
    if (!rowKeyToDel) return;
    delete this.data[rowKeyToDel];
  };

  /**
   * Gets ordered array of rowKeys based on each lsrClient's rowOrder.
   * @returns {string[]}
   */
  this.getRowOrder = () => {
    return _.orderBy(
      Object.entries(this.data).map(([rowKey, lsrClient]) => ({
        rowKey,
        rowOrder: +lsrClient.rowOrder,
      })),
      "rowOrder",
      "asc"
    ).map((ro) => ro.rowKey);
  };

  this.resetRowOrder = (rowKeys) => {
    if (!Array.isArray(rowKeys)) return;
    for (let i = 0; i < rowKeys.length; i++) {
      this.data[rowKeys[i]].rowOrder = i;
    }
  };

  /**
   * Add new WorkPerformed column to LaborHours.
   * @param {number} wpTabId - newly added wpTabId
   */
  this.addCol = (wpTabId) => {
    if (!wpTabId) return;
    Object.values(this.data).forEach((lsrClient) => {
      // This check against MAX_COLUMNS is also provided when new Wp is added.
      if (Object.keys(lsrClient.laborHours).length < MAX_COLUMNS)
        lsrClient.laborHours = {
          ...lsrClient.laborHours,
          [wpTabId]: {
            wpTabId: +wpTabId,
            regHours: null,
            otHours: null,
          },
        };
    });
  };

  /**
   * Delete WorkPerformed column from LaborHours.
   * @param {number} wpTabId - wpTabId for the tab being deleted
   */
  this.deleteCol = (wpTabId) => {
    if (!wpTabId) return;
    Object.values(this.data).forEach((lsr) => {
      delete lsr.laborHours[wpTabId];
    });
  };

  /**
   * Updates top-level fields in the provided labor set row.
   * @param {string} lsrKey - labor set rowKey
   * @param {import("../../_typedefs/adrV3TypeDefs").LaborSetRowClient} lsrFields
   *        - labor set row fields to update
   */
  this.update = (lsrKey, lsrFields) => {
    if (!lsrKey) return;
    if (!lsrFields || typeof lsrFields !== "object") return;
    for (const lsrField in lsrFields) {
      this.data[lsrKey][lsrField] = lsrFields[lsrField];
    }
  };

  this.showAllOvertimeRows = () => {
    for (const lsrKey in this.data) {
      this.data[lsrKey].showOvertimeRow = true;
    }
  };

  this.hideAllOvertimeRows = () => {
    for (const lsrKey in this.data) {
      this.data[lsrKey].showOvertimeRow = Object.values(
        this.data[lsrKey].laborHours
      ).some((lh) => lh.otHours > 0);
    }
  };

  this.updateRemarks = (lsrKey, remarks) => {
    if (!lsrKey) return;
    if (!this.data[lsrKey].remarks) return;
    this.data[lsrKey].remarks = {
      ...this.data[lsrKey].remarks,
      remarks,
    };
  };

  this.updateHours = (lsrKey, wpTabId, lhFields) => {
    if (!lsrKey || !wpTabId) return;
    if (!lhFields || typeof lhFields !== "object") return;

    const lhToEdit = this.data[lsrKey].laborHours[wpTabId];
    if (!lhToEdit) return;

    for (const lhField in lhFields) {
      lhToEdit[lhField] = lhFields[lhField];
    }
  };

  /**
   *
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   * @returns
   */
  this.applyChangesFromTentativeAgreement = (ta) => {
    if (!ta) return;

    for (const taLaborKey in ta.laborsClient) {
      // Every lsr field remains the same except hours.
      const lsrKeyToEdit = Object.keys(this.data).find(
        (lsrKey) =>
          this.data[lsrKey].laborId === ta.laborsClient[taLaborKey].laborId &&
          this.data[lsrKey].tradeId === ta.laborsClient[taLaborKey].tradeId
      );
      const lsrToEdit = lsrKeyToEdit in this.data ? this.data[lsrKeyToEdit] : null;
      if (!lsrToEdit) continue;
      lsrToEdit.laborHours[ta.wpTabId].regHours = +ta.laborsClient[taLaborKey].regHours;
      lsrToEdit.laborHours[ta.wpTabId].otHours = +ta.laborsClient[taLaborKey].otHours;
    }
  };
}

/****************************************************************************************
 * Provides methods operating on EquipmentSetClient.
 * EquipmentSet is a unique set of equipmentId, originally provided with
 * fetched Adr - but bootstrapped with more client-side properties immediately
 * upon successful fetch (see action creator). The param for this constructor MUST be
 * of type EquipmentSetClient - see typedefs for more info.
 * EquipmentSetClient data must be repackaged to the shape of EquipmentWorkPerformed prior
 * to uploading.
 * @param {Object.<string, import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient>} equipmentSetClient
 *        - EquipmentSetClients by rowKey as keys
 ***************************************************************************************/
export function EquipmentSetFactory(
  equipmentSetClient,
  rowKeyToCopy = null,
  doNotCopy = false
) {
  if (!equipmentSetClient || typeof equipmentSetClient !== "object")
    equipmentSetClient = {};

  if (!doNotCopy) {
    if (rowKeyToCopy) {
      /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient>} */
      this.data = {};
      for (const rowKey in equipmentSetClient) {
        this.data[rowKey] =
          rowKeyToCopy === rowKey
            ? _.cloneDeep(equipmentSetClient[rowKey])
            : equipmentSetClient[rowKey];
      }
    } else {
      /** @type {Object.<string, import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient>} */
      this.data = _.cloneDeep(equipmentSetClient);
    }
  } else this.data = equipmentSetClient;

  this.getMaxRowOrder = () =>
    Object.keys(this.data).length > 0
      ? Math.max(...Object.values(this.data).map((esrClient) => esrClient.rowOrder))
      : 0;

  this.getNextRowKeyValue = () => {
    const keys = Object.keys(this.data);
    return keys.length > 0
      ? Math.max(...keys.map((rowKey) => +rowKey.split("_")[1])) + 1
      : 1;
  };

  /**
   * Add new EquipmentSetClient row.
   * @param {number[]} allWpTabIds - List of all wpTabIds, to set each EquipmentHours
   * @param {?number} insertAt - Row index (0-based) at which to insert the new row
   * @param {?number} rowsToAdd - Number of rows to add. Defaults to 1 if not provided
   */
  this.addBlankRows = (allWpTabIds, insertAt = null, rowsToAdd = 1) => {
    if (!allWpTabIds || !Array.isArray(allWpTabIds)) return;
    if (insertAt != null && insertAt < 0) return;

    /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentHours} */
    let equipmentHours = {};

    allWpTabIds.forEach((wpTabId) => {
      equipmentHours[wpTabId] = {
        wpTabId: +wpTabId,
        regHours: "",
        otHours: "",
        operatorIds: [],
      };
    });

    // If insertAt is provided, increment all rowOrders that are greater than insertAt.
    if (insertAt != null)
      Object.values(this.data).forEach((esrClient) => {
        if (esrClient.rowOrder >= insertAt) esrClient.rowOrder += rowsToAdd;
      });

    const nextRowKeyValue = this.getNextRowKeyValue();
    const currMaxRowOrder = this.getMaxRowOrder();

    for (let i = 0; i < rowsToAdd; i++) {
      const rowKey = `esrKey_${nextRowKeyValue + i}`;

      // Insert new row
      this.data[rowKey] = {
        rowKey,
        equipmentId: 0,
        contractorId: 0,
        equipmentHours,
        remarks: { equipmentRemarksId: 0, remarks: "" },
        rowOrder: insertAt == null ? currMaxRowOrder + 1 + i : insertAt + i,
      };
    }
  };

  /**
   * Add multiple rows with preloaded equipment data
   * @param {number[]} allWpTabIds
   * @param {number} insertAt
   * @param {import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient[]} equipmentData
   *        - only equipmentId & contractorId used
   */
  this.addRows = (allWpTabIds, insertAt = null, equipmentData) => {
    if (!allWpTabIds || !Array.isArray(allWpTabIds)) return;
    if (!equipmentData || !Array.isArray(equipmentData)) return;
    if (insertAt != null && insertAt < 0) return;

    // If insertAt is provided, increment all rowOrders that are greater than insertAt.
    if (insertAt != null) {
      Object.values(this.data).forEach((esrClient) => {
        if (esrClient.rowOrder >= insertAt) esrClient.rowOrder += equipmentData.length;
      });
    }

    const nextRowKeyValue = this.getNextRowKeyValue();
    const currMaxRowOrder = this.getMaxRowOrder();

    for (let i = 0; i < equipmentData.length; i++) {
      const rowKey = `esrKey_${nextRowKeyValue + i}`;

      /** @type {import("../../_typedefs/adrV3TypeDefs").EquipmentHours} */
      const equipmentHours = {}; // Initialize
      allWpTabIds.forEach((wpTabId) => {
        equipmentHours[wpTabId] = {
          wpTabId: +wpTabId,
          regHours:
            equipmentData[i].equipmentHours && equipmentData[i].equipmentHours[wpTabId]
              ? equipmentData[i].equipmentHours[wpTabId].regHours || ""
              : "",
          otHours:
            equipmentData[i].equipmentHours && equipmentData[i].equipmentHours[wpTabId]
              ? equipmentData[i].equipmentHours[wpTabId].otHours || ""
              : "",
          operatorIds:
            equipmentData[i].equipmentHours && equipmentData[i].equipmentHours[wpTabId]
              ? equipmentData[i].equipmentHours[wpTabId].operatorIds || []
              : [],
        };

        this.data[rowKey] = {
          rowKey,
          equipmentId: equipmentData[i].equipmentId,
          contractorId: equipmentData[i].contractorId,
          equipmentHours,
          remarks: { equipmentRemarksId: 0, remarks: "" },
          rowOrder: insertAt == null ? currMaxRowOrder + 1 + i : insertAt + i,
        };
      });
    }
  };

  this.deleteRow = (rowKeyToDel) => {
    if (!rowKeyToDel) return;
    delete this.data[rowKeyToDel];
  };

  /**
   * Gets ordered array of rowKeys based on each lsrClient's rowOrder.
   * @returns {string[]}
   */
  this.getRowOrder = () => {
    return _.orderBy(
      Object.entries(this.data).map(([rowKey, esrClient]) => ({
        rowKey,
        rowOrder: +esrClient.rowOrder,
      })),
      "rowOrder",
      "asc"
    ).map((ro) => ro.rowKey);
  };

  this.resetRowOrder = (rowKeys) => {
    if (!Array.isArray(rowKeys)) return;
    for (let i = 0; i < rowKeys.length; i++) {
      this.data[rowKeys[i]].rowOrder = i;
    }
  };

  /**
   * Add new WorkPerformed column to EquipmentHours.
   * @param {number} wpTabId - newly added wpTabId
   */
  this.addCol = (wpTabId) => {
    if (!wpTabId) return;
    Object.values(this.data).forEach((esrClient) => {
      // This check against MAX_COLUMNS is also provided when new Wp is being added.
      if (Object.keys(esrClient.equipmentHours).length < MAX_COLUMNS) {
        esrClient.equipmentHours = {
          ...esrClient.equipmentHours,
          [wpTabId]: {
            wpTabId: +wpTabId,
            regHours: null,
            otHours: null,
            operatorIds: [],
          },
        };
      }
    });
  };

  /**
   * Delete WorkPerformed column from EquipmentHours.
   * @param {number} wpTabId - wpTabId for the tab being deleted
   */
  this.deleteCol = (wpTabId) => {
    if (!wpTabId) return;
    Object.values(this.data).forEach((esrClient) => {
      delete esrClient.equipmentHours[wpTabId];

      // MULTIPLE OPERATORS: FOR FUTURE USE
      // delete esrClient.operators[wpTabId];
    });
  };

  /**
   * Updates top-level fields in the provided equipment set row.
   * @param {string} esrKey - equipment set rowKey
   * @param {import("../../_typedefs/adrV3TypeDefs").EquipmentSetRowClient} esrFields
   *        - equipment set row fields to update
   */
  this.update = (esrKey, esrFields) => {
    if (!esrKey) return;
    if (!esrFields && typeof esrFields !== "object") return;
    for (const esrField in esrFields) {
      this.data[esrKey][esrField] = esrFields[esrField];
    }
  };

  this.showAllOvertimeRows = () => {
    for (const esrKey in this.data) {
      this.data[esrKey].showOvertimeRow = true;
    }
  };

  this.hideAllOvertimeRows = () => {
    for (const esrKey in this.data) {
      this.data[esrKey].showOvertimeRow = Object.values(
        this.data[esrKey].equipmentHours
      ).some((eh) => eh.otHours);
    }
  };

  this.updateRemarks = (esrKey, remarks) => {
    if (!esrKey) return;
    if (!this.data[esrKey].remarks) return;
    this.data[esrKey].remarks = {
      ...this.data[esrKey].remarks,
      remarks,
    };
  };

  this.updateHours = (esrKey, wpTabId, ehFields) => {
    if (!esrKey || !wpTabId) return;
    if (!ehFields || typeof ehFields !== "object") return;

    const ehToEdit = this.data[esrKey].equipmentHours[wpTabId];
    if (!ehToEdit) return;

    for (const ehField in ehFields) {
      ehToEdit[ehField] = ehFields[ehField];
    }
  };

  /**
   * Update operators for the selected equipment. Operators for multiple
   * workPerformed columns can be edited (provided by the operatorData param).
   * @param {string} esrKey
   * @param {Object.<number, number[]>} operatorData - keys are wpTabIds, values are arrays of laborIds
   */
  this.updateOperators = (esrKey, operatorData) => {
    if (!esrKey || !operatorData || typeof operatorData !== "object") return;

    for (const wpTabId in operatorData) {
      const ehToEdit = this.data[esrKey].equipmentHours[wpTabId];
      if (!ehToEdit) return;

      ehToEdit.operatorIds = [...operatorData[wpTabId]];
    }
  };

  /**
   * Applies any change to equipment hours made in the TA dialog
   * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} ta
   */
  this.applyChangesFromTentativeAgreement = (ta) => {
    if (!ta) return;

    for (const equipmentId in ta.equipmentClient) {
      const esrKeyToEdit = Object.keys(this.data).find(
        (esrKey) =>
          this.data[esrKey].equipmentId === ta.equipmentClient[equipmentId].equipmentId
      );
      const esrToEdit = esrKeyToEdit in this.data ? this.data[esrKeyToEdit] : null;
      if (!esrToEdit) continue;
      esrToEdit.equipmentHours[ta.wpTabId].regHours =
        +ta.equipmentClient[equipmentId].regHours;
      esrToEdit.equipmentHours[ta.wpTabId].otHours =
        +ta.equipmentClient[equipmentId].otHours;
    }
  };
}

/****************************************************************************************
 *
 * @param {import("../../_typedefs/adrV3TypeDefs").GeneralCommentClient[]} generalComments
 *        - array of generalCommentDto
 ***************************************************************************************/
export function GeneralCommentsFactory(generalComments = null) {
  if (!generalComments || !Array.isArray(generalComments)) generalComments = [];
  this.data = generalComments.map((gc) => ({
    ...gc,
    keywordIds: [...gc.keywordIds],
    commentAttachments: gc.commentAttachments.map((gca) => ({ ...gca })),
  }));

  this.initClient = (gcDtos) => {
    this.data = (gcDtos || []).map((gc, index) => ({
      ...gc,
      keywordIds: [...gc.keywordIds],
      commentAttachments: gc.commentAttachments.map((gca) => ({ ...gca })),
      clientId: index + 1,
    }));
  };

  this.addBlankRow = () => {
    const nextClientId =
      (this.data.length > 0 ? Math.max(...this.data.map((gc) => gc.clientId)) : 0) + 1;
    const newGc = {
      generalCommentId: 0,
      keywordIds: [],
      subject: "",
      narrative: "",
      isDeleted: false,
      commentAttachments: [],
      clientId: nextClientId,
    };
    this.data = [...this.data, newGc];
  };

  this.delete = (clientId) => {
    if (clientId == null) return;
    const gcIndex = this.data.findIndex((gc) => gc.clientId === clientId);
    if (gcIndex < 0) return;
    this.data = [...this.data.slice(0, gcIndex), ...this.data.slice(gcIndex + 1)];
  };

  this.update = (clientId, gcFields) => {
    if (clientId == null) return;
    if (!gcFields || typeof gcFields !== "object") return;

    let gcToUpdate = this.data.find((gc) => gc.clientId === clientId);
    if (!gcToUpdate) return;

    // If clientId comes in as part of gcFields to update, ignore.
    // clientId is only set in 1) initClient, or 2) adding new GC.
    for (const gcField in gcFields) {
      if (gcField !== "clientId") gcToUpdate[gcField] = gcFields[gcField];
    }
  };

  this.updateKeywords = (clientId, keywordIds) => {
    if (clientId == null) return;
    if (!keywordIds || !Array.isArray(keywordIds)) return;

    let gcToUpdate = this.data.find((gc) => gc.clientId === clientId);
    if (!gcToUpdate) return;

    gcToUpdate.keywordIds = [...keywordIds];
  };

  /**
   * Add metadata for new files to be added
   * @param {number} clientId
   * @param {import("../../_typedefs/adrV3TypeDefs").UploadedFiles} uploadedFiles
   */
  this.addAttachment = (clientId, uploadedFiles) => {
    if (clientId == null) return;
    if (!uploadedFiles || typeof uploadedFiles !== "object") return;

    const gc = this.data.find((gc) => gc.clientId === clientId);
    if (!gc) return;

    /** @type {import("../../_typedefs/adrV3TypeDefs").CommentAttachmentsDto[]} */
    let filesToAdd = [];
    const {
      filePaths,
      originalFileNames,
      imageThumbnailFilePaths,
      thumbnailBase64s,
      createdOn,
      descriptions,
    } = uploadedFiles;
    filePaths.forEach((filePath, index) => {
      filesToAdd.push({
        commentAttachmentId: 0,
        commentId: gc.commenntId,
        attachmentType: 0, // Default is set to Photo (General). User can change.
        originalFileName: originalFileNames[index],
        filePath,
        description: descriptions?.[index] ?? "",
        materialLotNumber: "",
        imageThumbnailFilePath: imageThumbnailFilePaths[index],
        thumbnailBase64: thumbnailBase64s[index],
        createdOn,
        isDeleted: false,
      });
    });

    gc.commentAttachments = [...gc.commentAttachments, ...filesToAdd];
  };

  this.toggleDeleteAttachment = (clientId, gcaIndex) => {
    if (clientId == null || gcaIndex == null) return;

    const gc = this.data.find((gc) => gc.clientId === clientId);
    if (!gc) return;
    const gca = gc.commentAttachments;
    if (!gca) return;

    gca[gcaIndex].isDeleted = !gca[gcaIndex].isDeleted;
  };

  /**
   * Updates gca metadata
   * @param {number} clientId
   * @param {number} gcaIndex
   * @param {import("../../_typedefs/adrV3TypeDefs").CommentAttachmentsDto} gcaFields
   */
  this.updateAttachment = (clientId, gcaIndex, gcaFields) => {
    if (clientId == null || gcaIndex == null) return;
    if (!gcaFields || typeof gcaFields !== "object") return;

    const gc = this.data.find((gc) => gc.clientId === clientId);
    if (!gc) return;
    const gca = gc.commentAttachments;
    if (!gca) return;

    for (const gcaField in gcaFields) {
      gca[gcaIndex][gcaField] = gcaFields[gcaField];
    }
  };
}

/****************************************************************************************
 *
 * @param {import("../../_typedefs/adrV3TypeDefs").CorrectionCommentClient[]} corrComm
 *        - array of correctionComments
 ***************************************************************************************/
export function CorrectionCommentFactory(corrComm = null) {
  if (!corrComm || !Array.isArray(corrComm)) corrComm = [];
  this.data = corrComm.map((cc) => ({ ...cc }));

  this.initClient = (corrCommDtos) => {
    this.data = (corrCommDtos || []).map((ccDto, index) => ({
      ...ccDto,
      isNew: false,
      fieldInfo: tryParseJSON(ccDto.fieldInfo),
      clientId: index + 1,
    }));
  };

  this.addBlankRow = (adrId, byUserId, section, fieldInfo = {}) => {
    const nextClientId =
      (this.data.length > 0 ? Math.max(...this.data.map((cc) => cc.clientId)) : 0) + 1;
    const blankCorrComm = {
      correctionCommentId: 0,
      assistantDailyReportId: adrId,
      commentDate: new Date(),
      byUserId,
      isAcknowledged: false,
      section,
      fieldInfo,
      comments: "",
      isNew: true,
      clientId: nextClientId,
    };

    this.data = [...this.data, blankCorrComm];
  };

  this.delete = (clientId) => {
    if (clientId == null) return;
    const ccIndex = this.data.findIndex((cc) => cc.clientId === clientId);
    if (ccIndex < 0) return;
    this.data = [...this.data.slice(0, ccIndex), ...this.data.slice(ccIndex + 1)];
  };

  this.update = (clientId, ccFields) => {
    if (clientId == null) return;
    if (!ccFields || typeof ccFields !== "object") return;

    let ccToUpdate = this.data.find((cc) => cc.clientId === clientId);
    if (!ccToUpdate) return;

    // If clientId comes in as part of gcFields to update, ignore.
    // clientId is only set in 1) initClient, or 2) adding new CC.
    for (const ccField in ccFields) {
      if (ccField !== "clientId") ccToUpdate[ccField] = ccFields[ccField];
    }
  };
}

/****************************************************************************************
 *
 * @param {import("../../_typedefs/adrV3TypeDefs").ClarificationCommentDto[]} clarComm
 *        - array of clarificationComments
 ***************************************************************************************/
export function ClarificationCommentFactory(clarComm = null) {
  if (!clarComm || !Array.isArray(clarComm)) clarComm = [];
  this.data = clarComm.map((cc) => ({ ...cc }));

  this.initClient = (clarCommDtos) => {
    this.data = (clarCommDtos || []).map((ccDto, index) => ({
      ...ccDto,
      keywordIds: [...ccDto.keywordIds],
      clientId: index + 1,
    }));
  };

  this.addBlankRow = (adrId, byUserId) => {
    const nextClientId =
      (this.data.length > 0 ? Math.max(...this.data.map((cc) => cc.clientId)) : 0) + 1;
    const blankClarComm = {
      clarificationCommentId: 0,
      assistantDailyReportId: adrId,
      commentDate: new Date(),
      byUserId,
      subject: "",
      keywordIds: [],
      comment: "",
      includeInCem4501: false,
      clientId: nextClientId,
    };

    this.data = [...this.data, blankClarComm];
  };

  this.delete = (clientId) => {
    if (clientId == null) return;
    const ccIndex = this.data.findIndex((cc) => cc.clientId === clientId);
    if (ccIndex < 0) return;
    this.data = [...this.data.slice(0, ccIndex), ...this.data.slice(ccIndex + 1)];
  };

  this.update = (clientId, ccFields) => {
    if (clientId == null) return;
    if (!ccFields || typeof ccFields !== "object") return;

    const ccToUpdate = this.data.find((cc) => cc.clientId === clientId);
    if (!ccToUpdate) return;

    // If clientId comes in as part of gcFields to update, ignore.
    // clientId is only set in 1) initClient, or 2) adding new CC.
    for (const ccField in ccFields) {
      if (ccField !== "clientId") ccToUpdate[ccField] = ccFields[ccField];
    }
  };

  this.updateKeywords = (clientId, keywordIds) => {
    if (clientId == null) return;
    if (!keywordIds || !Array.isArray(keywordIds)) return;

    const ccToUpdate = this.data.find((cc) => cc.clientId === clientId);
    if (!ccToUpdate) return;

    ccToUpdate.keywordIds = [...keywordIds];
  };
}

/****************************************************************************************
 *
 * @param {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient[]} tas
 *        - array of tentativeAgreementClients
 ***************************************************************************************/
export function TentativeAgreementsFactory(tas) {
  if (!tas || !Array.isArray(tas)) tas = [];

  /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient[]} */
  this.data = tas.map((ta) => ({
    ...ta,
    locations: _.cloneDeep(ta.locations || {}),
    contractorsClient: _.cloneDeep(ta.contractorsClient || {}),
    laborsClient: _.cloneDeep(ta.laborsClient || {}),
    equipmentClient: _.cloneDeep(ta.equipmentClient || {}),
    materialsClient: _.cloneDeep(ta.materialsClient || {}),
    attachments: (ta.attachments || []).map((taa) => ({ ...taa })),
  }));

  this.save = (ta) => {
    if (!ta) return;

    const existTaIndex = this.data.findIndex((t) => t.clientId === ta.clientId);
    if (existTaIndex < 0) return;
    for (let field in this.data[existTaIndex]) {
      if (field === "clientId") continue;
      if (field in ta) this.data[existTaIndex][field] = ta[field];
    }
  };

  /**
   * Adds a blank TA. A new clientId is assigned.
   * @param {import("../../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} adr
   * @param {number} wpTabId
   * @param {number} contractorId
   * @returns
   */
  this.addBlank = (adr, wpTabId, contractorId) => {
    if (!adr || !wpTabId) return;

    const nextClientId =
      (this.data.length > 0 ? Math.max(...this.data.map((ta) => ta.clientId)) : 0) + 1;

    /** @type {import("../../_typedefs/adrV3TypeDefs").TentativeAgreementClient} */
    const blankTa = {
      clientId: nextClientId,
      tentativeAgreementId: 0,
      assistantDailyReportId: adr.assistantDailyReportId,
      workPerformedId: adr.workPerformedClient[wpTabId].workPerformedId,
      wpTabId,
      isLocked: false,
      createdOn: Date.now(),
      workDescription: "",
      workDescriptionDraftState: "",
      locations: {},
      contractors: [contractorId],
      contractorsClient: {
        [contractorId]: {
          tentativeAgreementId: 0,
          contractorId,
          contractorRepSignature: "",
          isWetSignedByContractor: false,
          contractorRepSignedOn: null,
          contractorRepName: "",
          contractorRepRemarks: "",
          contractorRepEmail: "",
          isDisputedByContractor: false,
        },
      },
      laborsClient: {},
      equipmentClient: {},
      materialsClient: {},
      attachments: [],
      isCustomTa: false,
      caltransRepSignature: null,
      isWetSignedByCaltransRep: false,
      caltransRepSignedOn: null,
      caltransRepName: "",
      caltransRepRemarks: "",
      isDisputedByCaltransRep: false,
    };

    this.data = [...this.data, blankTa];
    this.newTaClientId = nextClientId;
  };

  /**
   * Do not use this for updating workPerformedId or contractors.
   * @param {number} clientId
   * @param {number} fields
   */
  this.update = (clientId, fields) => {
    if (!clientId) return;
    if (!fields || typeof fields !== "object") return;

    const taToUpdate = this.data.find((t) => t.clientId === clientId);
    if (!taToUpdate) return;

    for (const field in fields) {
      if (
        [
          "clientId",
          "wpTabId",
          "workPerformedId",
          "tentativeAgreementId",
          "assistantDailyReportId",
        ].includes(field)
      )
        continue;
      taToUpdate[field] = fields[field];
    }
  };

  this.delete = (clientId) => {
    if (!clientId) return;

    const taIndex = this.data.findIndex((ta) => ta.clientId === clientId);
    if (taIndex < 0) return;

    this.data = [...this.data.slice(0, taIndex), ...this.data.slice(taIndex + 1)];
  };
}
